import React, { useState, useEffect, useRef } from 'react';

// Unified Theme System with Dark and Light modes
const AghrbaThemes = {
  // Dark Theme
  dark: {
    colors: {
      // Primary colors - dark teal/navy blue
      primary: {
        main: '#0D2E4D',
        light: '#1C4670',
        dark: '#061B32',
        contrastText: '#FFFFFF',
      },
      // Secondary/accent colors - gold/yellow
      secondary: {
        main: '#f2c526',
        light: '#FFD859',
        dark: '#D9AD1F',
        contrastText: '#0D2E4D',
      },
      // Background colors
      background: {
        default: '#071725',
        paper: '#0A243A',
        light: '#102E4A',
        lighter: '#1A3C5E',
        card: '#0A243A',
        subtle: 'rgba(255, 255, 255, 0.03)',
      },
      // Text colors
      text: {
        primary: '#FFFFFF',
        secondary: '#B8C9D9',
        disabled: '#627D96',
        accent: '#f2c526',
        hint: '#8096AD',
      },
      // Status colors
      success: {
        main: '#4CAF50',
        light: '#81C784',
        dark: '#388E3C',
        contrastText: '#FFFFFF',
      },
      warning: {
        main: '#FF9800',
        light: '#FFB74D',
        dark: '#F57C00',
        contrastText: '#000000',
      },
      error: {
        main: '#F44336',
        light: '#E57373',
        dark: '#D32F2F',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#2196F3',
        light: '#64B5F6',
        dark: '#1976D2',
        contrastText: '#FFFFFF',
      },
      divider: 'rgba(184, 201, 217, 0.12)',
    },
    
    // Animation keyframes
    keyframes: {
      float: `
        @keyframes float {
          0% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0); }
        }
      `,
      glow: `
        @keyframes glow {
          0% { box-shadow: 0 0 5px rgba(228, 180, 76, 0.4), 0 0 10px rgba(228, 180, 76, 0.1); }
          50% { box-shadow: 0 0 20px rgba(228, 180, 76, 0.6), 0 0 15px rgba(228, 180, 76, 0.3); }
          100% { box-shadow: 0 0 5px rgba(228, 180, 76, 0.4), 0 0 10px rgba(228, 180, 76, 0.1); }
        }
      `,
      shine: `
        @keyframes shine {
          0% { background-position: -100% 0; }
          100% { background-position: 200% 0; }
        }
      `,
      gradient: `
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `,
      pulse: `
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
      `,
      fadeIn: `
        @keyframes fadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
      `,
    },
    
    // Shared styles
    styles: {
      gradientText: {
        background: `linear-gradient(90deg, #FFD859 0%, #f2c526 100%)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        color: 'transparent',
        display: 'inline-block',
      },
      gradientBorder: {
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          padding: '2px',
          background: `linear-gradient(135deg, #f2c526, #FFD859, #f2c526)`,
          backgroundSize: '300% 300%',
          animation: 'gradient 4s ease infinite',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          zIndex: 0,
        },
      },
      floatingAnimation: {
        animation: 'float 4s ease-in-out infinite',
      },
      glowingEffect: {
        animation: 'glow 3s infinite',
      },
      magicalBackground: {
        background: `linear-gradient(135deg, #071725 0%, #0A243A 100%)`,
      },
      scrollbarStyles: {
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#0A243A',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#1C4670',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#f2c526',
        },
      },
    },

    // Typography
    typography: {
      fontFamily: {
        primary: '"Poppins", sans-serif',
        display: '"Playfair Display", serif',
      }
    },

    // Elevation levels - shadows
    shadows: {
      sm: '0 2px 4px rgba(0, 0, 0, 0.3)',
      md: '0 4px 8px rgba(0, 0, 0, 0.3)',
      lg: '0 8px 16px rgba(0, 0, 0, 0.3)',
      xl: '0 12px 24px rgba(0, 0, 0, 0.3)',
      magical: '0 8px 16px rgba(0, 0, 0, 0.3), 0 0 20px rgba(242, 197, 38, 0.2)',
    },

    // Spacing scale
    spacing: {
      xs: '4px',
      sm: '8px',
      md: '16px',
      lg: '24px',
      xl: '32px',
      xxl: '48px',
      xxxl: '64px',
    },

    // Border radius scale
    borderRadius: {
      xs: '4px',
      sm: '8px',
      md: '12px',
      lg: '16px',
      xl: '24px',
      circle: '50%',
      pill: '9999px',
    },

    // Z-index scale
    zIndex: {
      tooltip: 1500,
      modal: 1400,
      popover: 1300,
      dropdown: 1200,
      header: 1100,
      base: 1,
    },
  },

  // Light Theme
  light: {
    colors: {
      // Primary colors - teal/navy blue (slightly lighter for light mode)
      primary: {
        main: '#0F3A61',
        light: '#2B5885',
        dark: '#072747',
        contrastText: '#FFFFFF',
      },
      // Secondary/accent colors - gold/yellow (same as dark theme)
      secondary: {
        main: '#f2c526',
        light: '#FFD859',
        dark: '#D9AD1F',
        contrastText: '#0F3A61',
      },
      // Background colors - light mode
      background: {
        default: '#F8FAFD',
        paper: '#FFFFFF',
        light: '#EEF2F7',
        lighter: '#F5F8FC',
        card: '#FFFFFF',
        subtle: 'rgba(15, 58, 97, 0.03)',
      },
      // Text colors - dark text for light mode
      text: {
        primary: '#0F3A61',
        secondary: '#4A6785',
        disabled: '#90A0B7',
        accent: '#D9AD1F',
        hint: '#6B8AAD',
      },
      // Status colors
      success: {
        main: '#43A047',
        light: '#81C784',
        dark: '#388E3C',
        contrastText: '#FFFFFF',
      },
      warning: {
        main: '#FB8C00',
        light: '#FFB74D',
        dark: '#F57C00',
        contrastText: '#000000',
      },
      error: {
        main: '#E53935',
        light: '#E57373',
        dark: '#D32F2F',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#1E88E5',
        light: '#64B5F6',
        dark: '#1976D2',
        contrastText: '#FFFFFF',
      },
      divider: 'rgba(15, 58, 97, 0.12)',
    },
    
    // Animation keyframes (same as dark theme)
    keyframes: {
      float: `
        @keyframes float {
          0% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0); }
        }
      `,
      glow: `
        @keyframes glow {
          0% { box-shadow: 0 0 5px rgba(228, 180, 76, 0.4), 0 0 10px rgba(228, 180, 76, 0.1); }
          50% { box-shadow: 0 0 20px rgba(228, 180, 76, 0.6), 0 0 15px rgba(228, 180, 76, 0.3); }
          100% { box-shadow: 0 0 5px rgba(228, 180, 76, 0.4), 0 0 10px rgba(228, 180, 76, 0.1); }
        }
      `,
      shine: `
        @keyframes shine {
          0% { background-position: -100% 0; }
          100% { background-position: 200% 0; }
        }
      `,
      gradient: `
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `,
      pulse: `
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
      `,
      fadeIn: `
        @keyframes fadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
      `,
    },
    
    // Shared styles for light theme
    styles: {
      gradientText: {
        background: `linear-gradient(90deg, #D9AD1F 0%, #f2c526 100%)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        color: 'transparent',
        display: 'inline-block',
      },
      gradientBorder: {
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          padding: '2px',
          background: `linear-gradient(135deg, #f2c526, #FFD859, #f2c526)`,
          backgroundSize: '300% 300%',
          animation: 'gradient 4s ease infinite',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          zIndex: 0,
        },
      },
      floatingAnimation: {
        animation: 'float 4s ease-in-out infinite',
      },
      glowingEffect: {
        animation: 'glow 3s infinite',
      },
      magicalBackground: {
        background: `linear-gradient(135deg, #F8FAFD 0%, #FFFFFF 100%)`,
      },
      scrollbarStyles: {
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#EEF2F7',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#2B5885',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#f2c526',
        },
      },
    },

    // Typography (same as dark theme)
    typography: {
      fontFamily: {
        primary: '"Poppins", sans-serif',
        display: '"Playfair Display", serif',
      }
    },

    // Elevation levels - shadows for light theme
    shadows: {
      sm: '0 2px 4px rgba(0, 0, 0, 0.1)',
      md: '0 4px 8px rgba(0, 0, 0, 0.12)',
      lg: '0 8px 16px rgba(0, 0, 0, 0.14)',
      xl: '0 12px 24px rgba(0, 0, 0, 0.16)',
      magical: '0 8px 16px rgba(0, 0, 0, 0.12), 0 0 20px rgba(242, 197, 38, 0.15)',
    },

    // Spacing scale (same as dark theme)
    spacing: {
      xs: '4px',
      sm: '8px',
      md: '16px',
      lg: '24px',
      xl: '32px',
      xxl: '48px',
      xxxl: '64px',
    },

    // Border radius scale (same as dark theme)
    borderRadius: {
      xs: '4px',
      sm: '8px',
      md: '12px',
      lg: '16px',
      xl: '24px',
      circle: '50%',
      pill: '9999px',
    },

    // Z-index scale (same as dark theme)
    zIndex: {
      tooltip: 1500,
      modal: 1400,
      popover: 1300,
      dropdown: 1200,
      header: 1100,
      base: 1,
    },
  }
};

// Create ThemeContext
const ThemeContext = React.createContext({
  themeMode: 'dark',
  theme: AghrbaThemes.dark,
  toggleTheme: () => {},
});

// Create mock icons for the design system
const MockIcons = {
  Search: ({ size = 20, color = 'currentColor' }) => (
    <div style={{width: size, height: size, borderRadius: '50%', border: `2px solid ${color}`}} />
  ),
  Add: ({ size = 20, color = 'currentColor' }) => (
    <div style={{width: size, height: size, position: 'relative'}}>
      <div style={{position: 'absolute', width: '100%', height: 2, background: color, top: size/2 - 1}}></div>
      <div style={{position: 'absolute', width: 2, height: '100%', background: color, left: size/2 - 1}}></div>
    </div>
  ),
  Menu: ({ size = 20, color = 'currentColor' }) => (
    <div style={{width: size, height: size * 0.7, display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
      <div style={{height: 2, background: color, width: '100%'}}></div>
      <div style={{height: 2, background: color, width: '100%'}}></div>
      <div style={{height: 2, background: color, width: '100%'}}></div>
    </div>
  ),
  Globe: ({ size = 20, color = 'currentColor' }) => (
    <div style={{
      width: size, 
      height: size, 
      borderRadius: '50%', 
      border: `2px solid ${color}`,
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        width: '100%',
        height: 2,
        background: color,
        top: size/2 - 1
      }}></div>
      <div style={{
        position: 'absolute',
        width: 2,
        height: '100%',
        background: color,
        left: size/2 - 1
      }}></div>
    </div>
  ),
  Moon: ({ size = 20, color = 'currentColor' }) => (
    <div style={{
      width: size, 
      height: size, 
      borderRadius: '50%',
      border: `2px solid ${color}`,
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{
        position: 'absolute',
        width: size * 0.8,
        height: size * 0.8,
        borderRadius: '50%',
        background: color,
        top: -size * 0.2,
        right: -size * 0.2
      }}></div>
    </div>
  ),
  Sun: ({ size = 20, color = 'currentColor' }) => (
    <div style={{
      width: size, 
      height: size, 
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        width: size * 0.5,
        height: size * 0.5,
        borderRadius: '50%',
        background: color
      }}></div>
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
        <div key={i} style={{
          position: 'absolute',
          width: 2,
          height: size * 0.2,
          background: color,
          transformOrigin: 'center bottom',
          transform: `rotate(${angle}deg) translateY(-${size * 0.35}px)`
        }}></div>
      ))}
    </div>
  ),
  Check: ({ size = 20, color = 'currentColor' }) => (
    <div style={{
      width: size, 
      height: size, 
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        width: size * 0.3,
        height: 2,
        background: color,
        top: size * 0.6,
        left: size * 0.2,
        transform: 'rotate(45deg)',
        transformOrigin: 'left center'
      }}></div>
      <div style={{
        position: 'absolute',
        width: size * 0.6,
        height: 2,
        background: color,
        top: size * 0.5,
        left: size * 0.35,
        transform: 'rotate(-45deg)',
        transformOrigin: 'left center'
      }}></div>
    </div>
  ),
  Chevron: ({ size = 20, color = 'currentColor', direction = 'down' }) => {
    const getRotation = () => {
      switch(direction) {
        case 'up': return '225deg';
        case 'right': return '315deg';
        case 'down': return '45deg';
        case 'left': return '135deg';
        default: return '45deg';
      }
    };
    
    return (
      <div style={{
        width: size, 
        height: size, 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          width: size * 0.4,
          height: size * 0.4,
          borderRight: `2px solid ${color}`,
          borderBottom: `2px solid ${color}`,
          transform: `rotate(${getRotation()})`
        }}></div>
      </div>
    );
  },
  Grid: ({ size = 20, color = 'currentColor' }) => (
    <div style={{
      width: size, 
      height: size, 
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gridTemplateRows: 'repeat(2, 1fr)',
      gap: 2
    }}>
      {[...Array(4)].map((_, i) => (
        <div key={i} style={{
          border: `1px solid ${color}`
        }}></div>
      ))}
    </div>
  ),
  List: ({ size = 20, color = 'currentColor' }) => (
    <div style={{
      width: size, 
      height: size, 
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    }}>
      {[...Array(3)].map((_, i) => (
        <div key={i} style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }}>
          <div style={{
            width: 4,
            height: 4,
            borderRadius: '50%',
            background: color
          }}></div>
          <div style={{
            width: size - 8,
            height: 2,
            background: color
          }}></div>
        </div>
      ))}
    </div>
  ),
  Calendar: ({ size = 20, color = 'currentColor' }) => (
    <div style={{
      width: size, 
      height: size, 
      border: `2px solid ${color}`,
      borderRadius: 2,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        height: size * 0.2,
        borderBottom: `2px solid ${color}`,
        display: 'flex',
        justifyContent: 'space-around'
      }}>
        {[...Array(2)].map((_, i) => (
          <div key={i} style={{
            width: 2,
            height: '100%',
            background: color
          }}></div>
        ))}
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gridTemplateRows: 'repeat(2, 1fr)',
        height: size * 0.8 - 2,
        padding: 2
      }}>
        {[...Array(4)].map((_, i) => (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              width: i === 0 ? 4 : 2,
              height: i === 0 ? 4 : 2,
              borderRadius: '50%',
              background: color
            }}></div>
          </div>
        ))}
      </div>
    </div>
  ),
  Team: ({ size = 20, color = 'currentColor' }) => (
    <div style={{
      width: size, 
      height: size, 
      position: 'relative'
    }}>
      <div style={{
        width: size * 0.4,
        height: size * 0.4,
        borderRadius: '50%',
        border: `2px solid ${color}`,
        position: 'absolute',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)'
      }}></div>
      <div style={{
        width: size * 0.3,
        height: size * 0.3,
        borderRadius: '50%',
        border: `2px solid ${color}`,
        position: 'absolute',
        bottom: 0,
        left: size * 0.15
      }}></div>
      <div style={{
        width: size * 0.3,
        height: size * 0.3,
        borderRadius: '50%',
        border: `2px solid ${color}`,
        position: 'absolute',
        bottom: 0,
        right: size * 0.15
      }}></div>
      <div style={{
        width: size * 0.6,
        height: size * 0.3,
        border: `2px solid ${color}`,
        borderTop: 'none',
        borderRadius: `0 0 ${size * 0.15}px ${size * 0.15}px`,
        position: 'absolute',
        top: size * 0.4,
        left: '50%',
        transform: 'translateX(-50%)'
      }}></div>
    </div>
  ),
  Kanban: ({ size = 20, color = 'currentColor' }) => (
    <div style={{
      width: size, 
      height: size, 
      display: 'flex',
      gap: 2
    }}>
      {[...Array(3)].map((_, i) => (
        <div key={i} style={{
          width: size / 3 - 2,
          height: '100%',
          border: `1px solid ${color}`,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          padding: 1
        }}>
          <div style={{
            width: '100%',
            height: 2,
            background: color,
            marginBottom: 2
          }}></div>
          {i < 2 && (
            <>
              <div style={{
                width: '100%',
                height: 3,
                background: color,
                opacity: 0.7,
                marginBottom: 2,
                borderRadius: 1
              }}></div>
              <div style={{
                width: '100%',
                height: 3,
                background: color,
                opacity: 0.4,
                borderRadius: 1
              }}></div>
            </>
          )}
        </div>
      ))}
    </div>
  ),
  Edit: ({ size = 20, color = 'currentColor' }) => (
    <div style={{
      width: size, 
      height: size, 
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        width: size * 0.7,
        height: size * 0.7,
        bottom: 0,
        left: 0,
        border: `2px solid ${color}`,
        borderTop: 'none',
        borderRight: 'none'
      }}></div>
      <div style={{
        position: 'absolute',
        width: 2,
        height: size * 0.7,
        background: color,
        top: 0,
        right: size * 0.15,
        transform: 'rotate(-45deg)',
        transformOrigin: 'bottom center'
      }}></div>
      <div style={{
        position: 'absolute',
        width: size * 0.3,
        height: size * 0.3,
        borderTop: `2px solid ${color}`,
        borderRight: `2px solid ${color}`,
        top: 0,
        right: 0
      }}></div>
    </div>
  ),
  Delete: ({ size = 20, color = 'currentColor' }) => (
    <div style={{
      width: size, 
      height: size, 
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        width: size * 0.7,
        height: size * 0.8,
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        border: `2px solid ${color}`,
        borderRadius: `0 0 ${size * 0.1}px ${size * 0.1}px`
      }}></div>
      <div style={{
        position: 'absolute',
        width: size * 0.4,
        height: 2,
        background: color,
        top: size * 0.15,
        left: '50%',
        transform: 'translateX(-50%)'
      }}></div>
      <div style={{
        position: 'absolute',
        width: size * 0.5,
        height: size * 0.1,
        border: `2px solid ${color}`,
        borderBottom: 'none',
        borderRadius: `${size * 0.1}px ${size * 0.1}px 0 0`,
        top: size * 0.05,
        left: '50%',
        transform: 'translateX(-50%)'
      }}></div>
      {[...Array(3)].map((_, i) => (
        <div key={i} style={{
          position: 'absolute',
          width: 2,
          height: size * 0.4,
          background: color,
          bottom: size * 0.1,
          left: `${(i + 1) * 25}%`
        }}></div>
      ))}
    </div>
  ),
  Star: ({ size = 20, color = 'currentColor', filled = false }) => {
    const points = [
      [size/2, 0],
      [size*0.6, size*0.35],
      [size, size*0.4],
      [size*0.7, size*0.6],
      [size*0.75, size],
      [size/2, size*0.75],
      [size*0.25, size],
      [size*0.3, size*0.6],
      [0, size*0.4],
      [size*0.4, size*0.35]
    ];
    
    const pointsStr = points.map(p => p.join(',')).join(' ');
    
    return (
      <div style={{
        width: size, 
        height: size, 
        position: 'relative'
      }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{position: 'absolute'}}>
          <polygon 
            points={pointsStr} 
            fill={filled ? color : 'none'} 
            stroke={color} 
            strokeWidth="1.5"
          />
        </svg>
      </div>
    );
  },
  Dots: ({ size = 20, color = 'currentColor', direction = 'vertical' }) => (
    <div style={{
      width: size, 
      height: size, 
      display: 'flex',
      flexDirection: direction === 'horizontal' ? 'row' : 'column',
      justifyContent: 'space-around',
      alignItems: 'center'
    }}>
      {[...Array(3)].map((_, i) => (
        <div key={i} style={{
          width: size * 0.15,
          height: size * 0.15,
          borderRadius: '50%',
          background: color
        }}></div>
      ))}
    </div>
  ),
  Bell: ({ size = 20, color = 'currentColor' }) => (
    <div style={{
      width: size, 
      height: size, 
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        width: size * 0.6,
        height: size * 0.6,
        borderRadius: `${size * 0.3}px ${size * 0.3}px 0 0`,
        border: `2px solid ${color}`,
        borderBottom: 'none',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)'
      }}></div>
      <div style={{
        position: 'absolute',
        width: size * 0.25,
        height: size * 0.25,
        borderRadius: '50%',
        border: `2px solid ${color}`,
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)'
      }}></div>
      <div style={{
        position: 'absolute',
        width: size * 0.7,
        height: size * 0.3,
        borderRadius: `0 0 ${size * 0.2}px ${size * 0.2}px`,
        border: `2px solid ${color}`,
        borderTop: 'none',
        bottom: size * 0.25,
        left: '50%',
        transform: 'translateX(-50%)'
      }}></div>
    </div>
  ),
  Filter: ({ size = 20, color = 'currentColor' }) => (
    <div style={{
      width: size, 
      height: size, 
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        width: '100%',
        height: 2,
        background: color,
        top: size * 0.2
      }}></div>
      <div style={{
        position: 'absolute',
        width: '100%',
        height: 2,
        background: color,
        top: size * 0.5
      }}></div>
      <div style={{
        position: 'absolute',
        width: '100%',
        height: 2,
        background: color,
        top: size * 0.8
      }}></div>
      <div style={{
        position: 'absolute',
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: color,
        top: size * 0.2 - 3,
        left: size * 0.3
      }}></div>
      <div style={{
        position: 'absolute',
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: color,
        top: size * 0.5 - 3,
        left: size * 0.7
      }}></div>
      <div style={{
        position: 'absolute',
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: color,
        top: size * 0.8 - 3,
        left: size * 0.5
      }}></div>
    </div>
  ),
  Sort: ({ size = 20, color = 'currentColor' }) => (
    <div style={{
      width: size, 
      height: size, 
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2
    }}>
      <div style={{
        width: 0,
        height: 0,
        borderLeft: `${size * 0.3}px solid transparent`,
        borderRight: `${size * 0.3}px solid transparent`,
        borderBottom: `${size * 0.3}px solid ${color}`
      }}></div>
      <div style={{
        width: 0,
        height: 0,
        borderLeft: `${size * 0.3}px solid transparent`,
        borderRight: `${size * 0.3}px solid transparent`,
        borderTop: `${size * 0.3}px solid ${color}`
      }}></div>
    </div>
  ),
  Upload: ({ size = 20, color = 'currentColor' }) => (
    <div style={{
      width: size, 
      height: size, 
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        width: 2,
        height: size * 0.6,
        background: color,
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)'
      }}></div>
      <div style={{
        position: 'absolute',
        width: size * 0.5,
        height: size * 0.5,
        borderTop: `2px solid ${color}`,
        borderLeft: `2px solid ${color}`,
        top: size * 0.2,
        left: '50%',
        transform: 'translateX(-50%) rotate(45deg)'
      }}></div>
    </div>
  ),
  Download: ({ size = 20, color = 'currentColor' }) => (
    <div style={{
      width: size, 
      height: size, 
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        width: 2,
        height: size * 0.6,
        background: color,
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)'
      }}></div>
      <div style={{
        position: 'absolute',
        width: size * 0.5,
        height: size * 0.5,
        borderBottom: `2px solid ${color}`,
        borderRight: `2px solid ${color}`,
        bottom: size * 0.2,
        left: '50%',
        transform: 'translateX(-50%) rotate(45deg)'
      }}></div>
    </div>
  ),
};

// Components
const Typography = ({ variant, children, style, theme, ...props }) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  
  const getStyles = () => {
    switch(variant) {
      case 'h1':
        return { 
          fontFamily: currentTheme.typography.fontFamily.display,
          fontSize: '2.5rem',
          fontWeight: 700,
          marginBottom: '0.5rem',
          color: currentTheme.colors.text.primary
        };
      case 'h2':
        return { 
          fontFamily: currentTheme.typography.fontFamily.display,
          fontSize: '2rem',
          fontWeight: 700,
          marginBottom: '0.5rem',
          color: currentTheme.colors.text.primary
        };
      case 'h3':
        return { 
          fontFamily: currentTheme.typography.fontFamily.display,
          fontSize: '1.75rem',
          fontWeight: 600,
          marginBottom: '0.5rem',
          color: currentTheme.colors.text.primary
        };
      case 'h4':
        return { 
          fontFamily: currentTheme.typography.fontFamily.display,
          fontSize: '1.5rem',
          fontWeight: 600,
          marginBottom: '0.5rem',
          color: currentTheme.colors.text.primary
        };
      case 'h5':
        return { 
          fontFamily: currentTheme.typography.fontFamily.primary,
          fontSize: '1.25rem',
          fontWeight: 600,
          marginBottom: '0.5rem',
          color: currentTheme.colors.text.primary
        };
      case 'h6':
        return { 
          fontFamily: currentTheme.typography.fontFamily.primary,
          fontSize: '1rem',
          fontWeight: 600,
          marginBottom: '0.5rem',
          color: currentTheme.colors.text.primary
        };
      case 'subtitle1':
        return { 
          fontFamily: currentTheme.typography.fontFamily.primary,
          fontSize: '1rem',
          fontWeight: 500,
          marginBottom: '0.5rem',
          color: currentTheme.colors.text.primary
        };
      case 'subtitle2':
        return { 
          fontFamily: currentTheme.typography.fontFamily.primary,
          fontSize: '0.875rem',
          fontWeight: 500,
          marginBottom: '0.5rem',
          color: currentTheme.colors.text.primary
        };
      case 'body1':
        return { 
          fontFamily: currentTheme.typography.fontFamily.primary,
          fontSize: '1rem',
          lineHeight: 1.6,
          marginBottom: '1rem',
          color: currentTheme.colors.text.primary
        };
      case 'body2':
        return { 
          fontFamily: currentTheme.typography.fontFamily.primary,
          fontSize: '0.875rem',
          lineHeight: 1.6,
          marginBottom: '1rem',
          color: currentTheme.colors.text.secondary
        };
      case 'caption':
        return { 
          fontFamily: currentTheme.typography.fontFamily.primary,
          fontSize: '0.75rem',
          lineHeight: 1.4,
          color: currentTheme.colors.text.secondary
        };
      case 'overline':
        return {
          fontFamily: currentTheme.typography.fontFamily.primary,
          fontSize: '0.75rem',
          fontWeight: 600,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: currentTheme.colors.text.secondary
        };
      default:
        return { 
          fontFamily: currentTheme.typography.fontFamily.primary,
          fontSize: '1rem',
          color: currentTheme.colors.text.primary
        };
    }
  };
  
  return (
    <div style={{ ...getStyles(), ...style }} {...props}>
      {children}
    </div>
  );
};

const Button = ({ 
  variant = 'contained', 
  color = 'primary', 
  size = 'medium',
  startIcon,
  endIcon,
  children,
  isGlowing = false,
  disabled = false,
  fullWidth = false,
  style,
  onClick,
  ...props 
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  
  const getSizeStyles = () => {
    switch(size) {
      case 'small':
        return { 
          padding: '0.5rem 1rem',
          fontSize: '0.75rem',
        };
      case 'large':
        return { 
          padding: '0.75rem 2rem',
          fontSize: '1rem',
        };
      default: // medium
        return { 
          padding: '0.625rem 1.5rem',
          fontSize: '0.875rem',
        };
    }
  };
  
  const getVariantStyles = () => {
    // Colors based on variant and color prop
    const colors = {
      primary: {
        main: currentTheme.colors.primary.main,
        light: currentTheme.colors.primary.light,
        contrastText: currentTheme.colors.primary.contrastText,
      },
      secondary: {
        main: currentTheme.colors.secondary.main,
        light: currentTheme.colors.secondary.light,
        contrastText: currentTheme.colors.secondary.contrastText,
      },
      success: {
        main: currentTheme.colors.success.main,
        light: currentTheme.colors.success.light,
        contrastText: currentTheme.colors.success.contrastText,
      },
      error: {
        main: currentTheme.colors.error.main,
        light: currentTheme.colors.error.light,
        contrastText: currentTheme.colors.error.contrastText,
      },
      warning: {
        main: currentTheme.colors.warning.main,
        light: currentTheme.colors.warning.light,
        contrastText: currentTheme.colors.warning.contrastText,
      },
      info: {
        main: currentTheme.colors.info.main,
        light: currentTheme.colors.info.light,
        contrastText: currentTheme.colors.info.contrastText,
      },
    };
    
    const selectedColor = colors[color] || colors.primary;
    
    switch(variant) {
      case 'contained':
        return { 
          background: `linear-gradient(45deg, ${selectedColor.main} 0%, ${selectedColor.light} 100%)`,
          color: selectedColor.contrastText,
          border: 'none',
          boxShadow: currentTheme.shadows.md,
          '&:hover': {
            boxShadow: currentTheme.shadows.lg,
            filter: 'brightness(1.1)',
          }
        };
      case 'outlined':
        return { 
          background: 'transparent',
          color: selectedColor.main,
          border: `2px solid ${selectedColor.main}`,
          '&:hover': {
            background: `${selectedColor.main}10`,
          }
        };
      case 'text':
        return { 
          background: 'transparent',
          color: selectedColor.main,
          border: 'none',
          '&:hover': {
            background: `${selectedColor.main}10`,
          }
        };
      case 'icon':
        return {
          background: 'transparent',
          color: selectedColor.main,
          border: 'none',
          padding: getSizeStyles().padding.split(' ')[0],
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 'auto',
          '&:hover': {
            background: `${selectedColor.main}10`,
          }
        };
      default:
        return {};
    }
  };
  
  const getDisabledStyles = () => {
    if (!disabled) return {};
    
    return {
      opacity: 0.6,
      cursor: 'not-allowed',
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none',
        filter: 'none',
        background: variant === 'contained' ? 
          `linear-gradient(45deg, ${currentTheme.colors.primary.main} 0%, ${currentTheme.colors.primary.light} 100%)` : 
          undefined,
      }
    };
  };
  
  const getMagicalEffects = () => {
    if (!isGlowing) return {};
    
    return {
      animation: 'glow 3s infinite',
      '@keyframes glow': {
        '0%': { boxShadow: `0 0 5px ${currentTheme.colors.secondary.main}40, 0 0 10px ${currentTheme.colors.secondary.main}10` },
        '50%': { boxShadow: `0 0 20px ${currentTheme.colors.secondary.main}60, 0 0 15px ${currentTheme.colors.secondary.main}30` },
        '100%': { boxShadow: `0 0 5px ${currentTheme.colors.secondary.main}40, 0 0 10px ${currentTheme.colors.secondary.main}10` },
      },
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
        transition: 'left 0.7s',
      },
      '&:hover::before': {
        left: '100%',
      },
    };
  };
  
  // Combine all styles
  const buttonStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontWeight: 600,
    borderRadius: currentTheme.borderRadius.pill,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    gap: '0.5rem',
    width: fullWidth ? '100%' : 'auto',
    boxSizing: 'border-box',
    ...getSizeStyles(),
    ...getVariantStyles(),
    ...getDisabledStyles(),
    ...getMagicalEffects(),
    ...style
  };
  
  return (
    <button 
      style={buttonStyles} 
      disabled={disabled} 
      onClick={disabled ? undefined : onClick}
      {...props}
    >
      {startIcon && <span className="button-icon">{startIcon}</span>}
      {children}
      {endIcon && <span className="button-icon">{endIcon}</span>}
    </button>
  );
};

const IconButton = ({
  color = 'primary',
  size = 'medium',
  children,
  disabled = false,
  isGlowing = false,
  style,
  onClick,
  ...props
}) => {
  return (
    <Button
      variant="icon"
      color={color}
      size={size}
      disabled={disabled}
      isGlowing={isGlowing}
      onClick={onClick}
      style={style}
      {...props}
    >
      {children}
    </Button>
  );
};

const Card = ({ 
  children, 
  isGlowing = false, 
  isFloating = false,
  gradientBorder = false,
  noPadding = false,
  style,
  ...props 
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  
  const cardStyles = {
    backgroundColor: currentTheme.colors.background.card,
    borderRadius: currentTheme.borderRadius.lg,
    padding: noPadding ? 0 : '20px',
    overflow: 'hidden',
    boxShadow: isGlowing ? currentTheme.shadows.magical : currentTheme.shadows.md,
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    position: 'relative',
    ...(isFloating && {
      animation: 'float 4s ease-in-out infinite',
    }),
    '&:hover': {
      transform: 'translateY(-6px)',
      boxShadow: isGlowing ? `${currentTheme.shadows.magical}, 0 12px 24px rgba(0, 0, 0, 0.2)` : currentTheme.shadows.lg,
    },
    ...style
  };
  
  // Gradient border effect
  const gradientBorderStyles = gradientBorder ? {
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      padding: '2px',
      background: `linear-gradient(135deg, ${currentTheme.colors.secondary.main}, ${currentTheme.colors.secondary.light}, ${currentTheme.colors.secondary.main})`,
      backgroundSize: '300% 300%',
      animation: 'gradient 5s ease infinite',
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      zIndex: 0,
    }
  } : {};
  
  return (
    <div style={{...cardStyles, ...gradientBorderStyles}} {...props}>
      <div style={{ position: 'relative', zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
};

const TeamManagementView = ({
  members = [],
  projects = [],
  onMemberClick,
  onProjectClick,
  onAssignProject,
  showAvailability = true,
  showWorkload = true,
  style,
  ...props
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  const [expandedMember, setExpandedMember] = useState(null);
  const [filterRole, setFilterRole] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Get all unique roles
  const roles = [...new Set(members.map(member => member.role))];
  
  // Filter members based on role and search term
  const filteredMembers = members.filter(member => {
    const matchesRole = !filterRole || member.role === filterRole;
    const matchesSearch = !searchTerm || 
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      member.role.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesRole && matchesSearch;
  });
  
  // Handle member click
  const handleMemberClick = (member) => {
    if (expandedMember === member.id) {
      setExpandedMember(null);
    } else {
      setExpandedMember(member.id);
      if (onMemberClick) onMemberClick(member);
    }
  };
  
  // Handle project click
  const handleProjectClick = (project) => {
    if (onProjectClick) onProjectClick(project);
  };
  
  // Calculate member workload percentage
  const calculateWorkload = (member) => {
    const assignedProjects = projects.filter(project => 
      project.assignees && project.assignees.includes(member.id)
    );
    
    if (assignedProjects.length === 0) return 0;
    
    // Simple calculation based on number of projects and their complexity
    const totalWorkload = assignedProjects.reduce((sum, project) => {
      const complexity = project.complexity || 1; // Default to 1 if not specified
      return sum + complexity;
    }, 0);
    
    // Normalize to percentage (assuming 5 is the max reasonable workload)
    return Math.min(Math.round((totalWorkload / 5) * 100), 100);
  };
  
  const containerStyles = {
    backgroundColor: currentTheme.colors.background.card,
    borderRadius: currentTheme.borderRadius.lg,
    boxShadow: currentTheme.shadows.md,
    overflow: 'hidden',
    ...style
  };
  
  const headerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    borderBottom: `1px solid ${currentTheme.colors.divider}`
  };
  
  const headerTitleStyles = {
    fontFamily: currentTheme.typography.fontFamily.display,
    fontSize: '1.5rem',
    fontWeight: 600,
    color: currentTheme.colors.text.primary,
    margin: 0
  };
  
  const filterContainerStyles = {
    display: 'flex',
    gap: '16px',
    alignItems: 'center'
  };
  
  const teamListStyles = {
    padding: '16px',
    maxHeight: '600px',
    overflow: 'auto',
    ...currentTheme.styles.scrollbarStyles
  };
  
  const memberCardStyles = {
    backgroundColor: currentTheme.colors.background.paper,
    borderRadius: currentTheme.borderRadius.md,
    boxShadow: currentTheme.shadows.sm,
    marginBottom: '16px',
    overflow: 'hidden',
    transition: 'box-shadow 0.2s ease'
  };
  
  const memberHeaderStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: currentTheme.colors.background.light
    }
  };
  
  const memberInfoStyles = {
    flex: 1,
    overflow: 'hidden'
  };
  
  const memberNameStyles = {
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: '1rem',
    fontWeight: 600,
    color: currentTheme.colors.text.primary,
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };
  
  const memberRoleStyles = {
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: '0.875rem',
    color: currentTheme.colors.text.secondary,
    margin: '4px 0 0 0'
  };
  
  const memberMetricsStyles = {
    display: 'flex',
    gap: '16px',
    alignItems: 'center'
  };
  
  const memberDetailsStyles = {
    padding: '0 16px 16px',
    borderTop: `1px solid ${currentTheme.colors.divider}`,
    animation: 'fadeIn 0.3s ease'
  };
  
  const projectsGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '12px',
    marginTop: '16px'
  };
  
  const projectCardStyles = {
    backgroundColor: currentTheme.colors.background.light,
    borderRadius: currentTheme.borderRadius.sm,
    padding: '12px',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: currentTheme.shadows.md
    }
  };
  
  const projectTitleStyles = {
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: '0.875rem',
    fontWeight: 600,
    color: currentTheme.colors.text.primary,
    margin: '0 0 4px 0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };
  
  const projectDetailsStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '0.75rem',
    color: currentTheme.colors.text.secondary
  };
  
  const emptyStateStyles = {
    padding: '32px 16px',
    textAlign: 'center',
    color: currentTheme.colors.text.secondary,
    fontSize: '0.875rem'
  };
  
  return (
    <div style={containerStyles} {...props}>
      <div style={headerStyles}>
        <h2 style={headerTitleStyles}>Team Management</h2>
        
        <div style={filterContainerStyles}>
          <TextField
            placeholder="Search members..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            startIcon={<MockIcons.Search size={16} />}
            style={{ width: '200px' }}
          />
          
          <select 
            style={{
              padding: '8px 12px',
              borderRadius: currentTheme.borderRadius.sm,
              border: `1px solid ${currentTheme.colors.divider}`,
              backgroundColor: currentTheme.colors.background.paper,
              color: currentTheme.colors.text.primary,
              fontSize: '0.875rem',
              cursor: 'pointer'
            }}
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
          >
            <option value="">All Roles</option>
            {roles.map((role, index) => (
              <option key={index} value={role}>{role}</option>
            ))}
          </select>
        </div>
      </div>
      
      <div style={teamListStyles}>
        {filteredMembers.length === 0 ? (
          <div style={emptyStateStyles}>
            No team members match your filters
          </div>
        ) : (
          filteredMembers.map((member) => {
            const isExpanded = expandedMember === member.id;
            const workload = calculateWorkload(member);
            const memberProjects = projects.filter(project => 
              project.assignees && project.assignees.includes(member.id)
            );
            
            return (
              <div key={member.id} style={memberCardStyles}>
                <div 
                  style={memberHeaderStyles}
                  onClick={() => handleMemberClick(member)}
                >
                  <Avatar 
                    size="large"
                    src={member.avatar}
                    status={member.status}
                  >
                    {!member.avatar && member.name.split(' ').map(n => n[0]).join('')}
                  </Avatar>
                  
                  <div style={memberInfoStyles}>
                    <h3 style={memberNameStyles}>{member.name}</h3>
                    <div style={memberRoleStyles}>{member.role}</div>
                  </div>
                  
                  <div style={memberMetricsStyles}>
                    {showAvailability && (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.75rem', color: currentTheme.colors.text.secondary }}>
                          Availability
                        </div>
                        <Badge
                          color={member.status === 'online' ? 'success' : 'warning'}
                          variant="soft"
                        >
                          {member.status === 'online' ? 'Available' : 'Busy'}
                        </Badge>
                      </div>
                    )}
                    
                    {showWorkload && (
                      <div style={{ textAlign: 'center', minWidth: '80px' }}>
                        <div style={{ fontSize: '0.75rem', color: currentTheme.colors.text.secondary }}>
                          Workload
                        </div>
                        <ProgressBar
                          value={workload}
                          size="small"
                          color={workload > 80 ? 'error' : workload > 60 ? 'warning' : 'success'}
                          label
                          style={{ width: '80px' }}
                        />
                      </div>
                    )}
                    
                    <MockIcons.Chevron 
                      direction={isExpanded ? 'up' : 'down'} 
                      size={20} 
                      color={currentTheme.colors.text.secondary}
                    />
                  </div>
                </div>
                
                {isExpanded && (
                  <div style={memberDetailsStyles}>
                    <Divider />
                    
                    <div style={{ marginTop: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" style={{ color: currentTheme.colors.text.primary }}>
                          Assigned Projects ({memberProjects.length})
                        </Typography>
                        
                        {onAssignProject && (
                          <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            startIcon={<MockIcons.Add size={16} />}
                            onClick={() => onAssignProject(member)}
                          >
                            Assign Project
                          </Button>
                        )}
                      </div>
                      
                      {memberProjects.length === 0 ? (
                        <div style={{ 
                          padding: '16px', 
                          textAlign: 'center', 
                          color: currentTheme.colors.text.secondary,
                          fontSize: '0.875rem'
                        }}>
                          No projects assigned
                        </div>
                      ) : (
                        <div style={projectsGridStyles}>
                          {memberProjects.map((project) => (
                            <div 
                              key={project.id}
                              style={projectCardStyles}
                              onClick={() => handleProjectClick(project)}
                            >
                              <h4 style={projectTitleStyles}>{project.title}</h4>
                              
                              <div style={projectDetailsStyles}>
                                <div>
                                  {project.status && (
                                    <Badge
                                      color={
                                        project.status === 'completed' ? 'success' :
                                        project.status === 'in-progress' ? 'warning' :
                                        project.status === 'blocked' ? 'error' : 'info'
                                      }
                                      size="small"
                                      variant="soft"
                                    >
                                      {project.status}
                                    </Badge>
                                  )}
                                </div>
                                
                                <div>
                                  {project.dueDate && new Date(project.dueDate).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// Enhanced Design System Showcase
const AghrbaDesignSystem = () => {
  const [themeMode, setThemeMode] = useState('dark');
  const [activeTab, setActiveTab] = useState(0);
  
  // Create theme context value
  const themeContextValue = {
    themeMode,
    theme: themeMode === 'dark' ? AghrbaThemes.dark : AghrbaThemes.light,
    toggleTheme: () => setThemeMode(themeMode === 'dark' ? 'light' : 'dark')
  };
  
  const tabs = [
    { label: 'Overview', content: 'overview' },
    { label: 'Components', content: 'components' },
    { label: 'Table View', content: 'tableView' },
    { label: 'Grid View', content: 'gridView' },
    { label: 'Kanban View', content: 'kanbanView' },
    { label: 'Calendar', content: 'calendar' },
    { label: 'Team', content: 'team' },
  ];
  
  // Sample Data for Components
  const sampleProjects = [
    { 
      id: 'p1', 
      title: 'Website Redesign', 
      description: 'Redesign the company website with a modern look and improved UX.',
      tags: ['design', 'frontend'],
      status: 'active',
      progress: 65,
      date: 'Dec 31, 2025'
    },
    { 
      id: 'p2', 
      title: 'Mobile App Development', 
      description: 'Create a cross-platform mobile app with real-time features.',
      tags: ['mobile', 'react-native'],
      status: 'high priority',
      progress: 25,
      date: 'Nov 15, 2025'
    },
    { 
      id: 'p3', 
      title: 'Marketing Campaign', 
      description: 'Plan and execute Q4 marketing campaign across digital channels.',
      tags: ['marketing', 'content'],
      status: 'pending',
      progress: 10,
      date: 'Jan 15, 2026'
    },
    { 
      id: 'p4', 
      title: 'API Integration', 
      description: 'Integrate payment gateway APIs into the existing system.',
      tags: ['backend', 'integration'],
      status: 'completed',
      progress: 100,
      date: 'Oct 20, 2025'
    }
  ];
  
  const sampleTableData = [
    { id: 1, name: 'John Doe', email: 'john.doe@example.com', role: 'Developer', status: 'Active', joined: '2022-05-15' },
    { id: 2, name: 'Jane Smith', email: 'jane.smith@example.com', role: 'Designer', status: 'Active', joined: '2023-02-10' },
    { id: 3, name: 'Robert Johnson', email: 'robert.j@example.com', role: 'Manager', status: 'Away', joined: '2021-11-08' },
    { id: 4, name: 'Emily Davis', email: 'emily.d@example.com', role: 'Developer', status: 'Active', joined: '2023-07-22' },
    { id: 5, name: 'Michael Brown', email: 'michael.b@example.com', role: 'Tester', status: 'Inactive', joined: '2022-09-30' },
    { id: 6, name: 'Sarah Wilson', email: 'sarah.w@example.com', role: 'Designer', status: 'Active', joined: '2023-01-15' },
    { id: 7, name: 'David Miller', email: 'david.m@example.com', role: 'Developer', status: 'Active', joined: '2022-03-18' }
  ];
  
  const sampleTableColumns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'role', label: 'Role', sortable: true },
    { key: 'status', label: 'Status', sortable: true, render: (value) => (
      <Badge 
        color={value === 'Active' ? 'success' : value === 'Away' ? 'warning' : 'error'}
        variant="soft"
      >
        {value}
      </Badge>
    )},
    { key: 'joined', label: 'Joined Date', sortable: true }
  ];
  
  const sampleKanbanColumns = [
    {
      id: 'todo',
      title: 'To Do',
      cards: [
        { id: 'card1', title: 'Research competitors', description: 'Analyze top 5 competitors in the market' },
        { id: 'card2', title: 'Create wireframes', description: 'Design initial wireframes for homepage', tags: [{ label: 'Design', color: 'secondary' }] },
        { id: 'card3', title: 'Setup development environment', description: 'Install and configure necessary tools' }
      ]
    },
    {
      id: 'inProgress',
      title: 'In Progress',
      cards: [
        { id: 'card4', title: 'Implement authentication', description: 'Create login and registration flows', tags: [{ label: 'Backend', color: 'primary' }] },
        { id: 'card5', title: 'Design user dashboard', description: 'Create UI for user statistics and activity' }
      ]
    },
    {
      id: 'review',
      title: 'Review',
      cards: [
        { id: 'card6', title: 'API documentation', description: 'Write documentation for all REST endpoints', tags: [{ label: 'Documentation', color: 'info' }] }
      ]
    },
    {
      id: 'done',
      title: 'Done',
      cards: [
        { id: 'card7', title: 'Project setup', description: 'Initialize repository and project structure', tags: [{ label: 'Setup', color: 'success' }] },
        { id: 'card8', title: 'Requirements gathering', description: 'Collect and document project requirements' }
      ]
    }
  ];
  
  const sampleCalendarEvents = [
    {
      id: 'event1',
      title: 'Team Meeting',
      start: new Date(2025, 4, 5, 10, 0),
      end: new Date(2025, 4, 5, 11, 30),
      color: '#2196F3'
    },
    {
      id: 'event2',
      title: 'Project Deadline',
      start: new Date(2025, 4, 15, 0, 0),
      end: new Date(2025, 4, 15, 23, 59),
      color: '#F44336'
    },
    {
      id: 'event3',
      title: 'Client Meeting',
      start: new Date(2025, 4, 10, 14, 0),
      end: new Date(2025, 4, 10, 15, 0),
      color: '#4CAF50'
    }
  ];
  
  const sampleTeamMembers = [
    {
      id: 'member1',
      name: 'John Smith',
      role: 'Senior Developer',
      status: 'online',
      avatar: null
    },
    {
      id: 'member2',
      name: 'Emily Johnson',
      role: 'UI/UX Designer',
      status: 'offline',
      avatar: null
    },
    {
      id: 'member3',
      name: 'Michael Brown',
      role: 'Project Manager',
      status: 'online',
      avatar: null
    },
    {
      id: 'member4',
      name: 'Sarah Wilson',
      role: 'Backend Developer',
      status: 'online',
      avatar: null
    }
  ];
  
  const sampleTeamProjects = [
    {
      id: 'project1',
      title: 'E-commerce Platform',
      status: 'in-progress',
      dueDate: '2025-07-15',
      assignees: ['member1', 'member2'],
      complexity: 4
    },
    {
      id: 'project2',
      title: 'Mobile Banking App',
      status: 'blocked',
      dueDate: '2025-08-30',
      assignees: ['member1', 'member3', 'member4'],
      complexity: 5
    },
    {
      id: 'project3',
      title: 'Internal Dashboard',
      status: 'completed',
      dueDate: '2025-05-01',
      assignees: ['member2', 'member4'],
      complexity: 3
    }
  ];
  
  // Main container style based on theme
  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: themeContextValue.theme.colors.background.default,
    fontFamily: themeContextValue.theme.typography.fontFamily.primary,
    color: themeContextValue.theme.colors.text.primary,
    padding: '40px 0',
    transition: 'background-color 0.3s ease, color 0.3s ease'
  };
  
  // Render specific tab content
  const renderTabContent = () => {
    switch (tabs[activeTab].content) {
      case 'overview':
        return (
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <Card style={{ padding: '30px' }}>
              <Typography variant="h4" style={{ marginBottom: '20px' }}>
                Enhanced Aghrba Design System
              </Typography>
              
              <Typography variant="body1" style={{ marginBottom: '24px' }}>
                This enhanced version of the Aghrba Design System includes a comprehensive set of UI components and views designed for modern applications. The system features both dark and light themes, along with magical accents to create an elegant and luxurious user experience.
              </Typography>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                <div>
                  <Typography variant="h6" style={{ marginBottom: '12px', color: themeContextValue.theme.colors.secondary.main }}>
                    Core Features
                  </Typography>
                  <ul style={{ paddingLeft: '20px', margin: 0 }}>
                    <li><Typography variant="body2" style={{ marginBottom: '8px' }}>Unified theme system with dark/light mode support</Typography></li>
                    <li><Typography variant="body2" style={{ marginBottom: '8px' }}>Consistent spacing and elevation scale</Typography></li>
                    <li><Typography variant="body2" style={{ marginBottom: '8px' }}>Responsive component design</Typography></li>
                    <li><Typography variant="body2" style={{ marginBottom: '8px' }}>Accessible color contrasts</Typography></li>
                    <li><Typography variant="body2">Magical interactions and animations</Typography></li>
                  </ul>
                </div>
                
                <div>
                  <Typography variant="h6" style={{ marginBottom: '12px', color: themeContextValue.theme.colors.secondary.main }}>
                    Component Library
                  </Typography>
                  <ul style={{ paddingLeft: '20px', margin: 0 }}>
                    <li><Typography variant="body2" style={{ marginBottom: '8px' }}>Basic UI elements (buttons, inputs, cards)</Typography></li>
                    <li><Typography variant="body2" style={{ marginBottom: '8px' }}>Data display (tables, grids, lists)</Typography></li>
                    <li><Typography variant="body2" style={{ marginBottom: '8px' }}>Navigation components</Typography></li>
                    <li><Typography variant="body2" style={{ marginBottom: '8px' }}>Feedback indicators</Typography></li>
                    <li><Typography variant="body2">Specialized views (kanban, calendar, team management)</Typography></li>
                  </ul>
                </div>
                
                <div>
                  <Typography variant="h6" style={{ marginBottom: '12px', color: themeContextValue.theme.colors.secondary.main }}>
                    Implementation
                  </Typography>
                  <ul style={{ paddingLeft: '20px', margin: 0 }}>
                    <li><Typography variant="body2" style={{ marginBottom: '8px' }}>React-based component library</Typography></li>
                    <li><Typography variant="body2" style={{ marginBottom: '8px' }}>CSS-in-JS styling approach</Typography></li>
                    <li><Typography variant="body2" style={{ marginBottom: '8px' }}>Context API for theme management</Typography></li>
                    <li><Typography variant="body2" style={{ marginBottom: '8px' }}>Compositional component architecture</Typography></li>
                    <li><Typography variant="body2">Responsive breakpoints</Typography></li>
                  </ul>
                </div>
              </div>
              
              <Divider style={{ margin: '32px 0' }} />
              
              <Typography variant="h5" style={{ marginBottom: '20px' }}>
                Color Palette
              </Typography>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '20px',
                marginBottom: '32px'
              }}>
                {/* Primary Colors */}
                <Card>
                  <div style={{ 
                    height: '120px', 
                    backgroundColor: themeContextValue.theme.colors.primary.main,
                    display: 'flex',
                    alignItems: 'flex-end',
                    padding: '20px'
                  }}>
                    <Typography variant="h6" style={{ color: themeContextValue.theme.colors.primary.contrastText }}>Primary</Typography>
                  </div>
                  <div style={{ padding: '20px' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '10px'
                    }}>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '4px',
                        backgroundColor: themeContextValue.theme.colors.primary.main,
                        marginRight: '10px'
                      }} />
                      <Typography variant="body2">main: {themeContextValue.theme.colors.primary.main}</Typography>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '10px'
                    }}>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '4px',
                        backgroundColor: themeContextValue.theme.colors.primary.light,
                        marginRight: '10px'
                      }} />
                      <Typography variant="body2">light: {themeContextValue.theme.colors.primary.light}</Typography>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center'
                    }}>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '4px',
                        backgroundColor: themeContextValue.theme.colors.primary.dark,
                        marginRight: '10px'
                      }} />
                      <Typography variant="body2">dark: {themeContextValue.theme.colors.primary.dark}</Typography>
                    </div>
                  </div>
                </Card>
                
                {/* Secondary Colors */}
                <Card>
                  <div style={{ 
                    height: '120px', 
                    backgroundColor: themeContextValue.theme.colors.secondary.main,
                    display: 'flex',
                    alignItems: 'flex-end',
                    padding: '20px'
                  }}>
                    <Typography variant="h6" style={{ color: themeContextValue.theme.colors.secondary.contrastText }}>Secondary</Typography>
                  </div>
                  <div style={{ padding: '20px' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '10px'
                    }}>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '4px',
                        backgroundColor: themeContextValue.theme.colors.secondary.main,
                        marginRight: '10px'
                      }} />
                      <Typography variant="body2">main: {themeContextValue.theme.colors.secondary.main}</Typography>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '10px'
                    }}>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '4px',
                        backgroundColor: themeContextValue.theme.colors.secondary.light,
                        marginRight: '10px'
                      }} />
                      <Typography variant="body2">light: {themeContextValue.theme.colors.secondary.light}</Typography>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center'
                    }}>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '4px',
                        backgroundColor: themeContextValue.theme.colors.secondary.dark,
                        marginRight: '10px'
                      }} />
                      <Typography variant="body2">dark: {themeContextValue.theme.colors.secondary.dark}</Typography>
                    </div>
                  </div>
                </Card>
                
                {/* Status Colors */}
                <Card>
                  <div style={{ 
                    height: '120px', 
                    background: `linear-gradient(135deg, ${themeContextValue.theme.colors.success.main} 0%, ${themeContextValue.theme.colors.error.main} 100%)`,
                    display: 'flex',
                    alignItems: 'flex-end',
                    padding: '20px'
                  }}>
                    <Typography variant="h6" style={{ color: '#FFFFFF' }}>Status Colors</Typography>
                  </div>
                  <div style={{ padding: '20px' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '10px'
                    }}>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '4px',
                        backgroundColor: themeContextValue.theme.colors.success.main,
                        marginRight: '10px'
                      }} />
                      <Typography variant="body2">success: {themeContextValue.theme.colors.success.main}</Typography>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '10px'
                    }}>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '4px',
                        backgroundColor: themeContextValue.theme.colors.warning.main,
                        marginRight: '10px'
                      }} />
                      <Typography variant="body2">warning: {themeContextValue.theme.colors.warning.main}</Typography>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center'
                    }}>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '4px',
                        backgroundColor: themeContextValue.theme.colors.error.main,
                        marginRight: '10px'
                      }} />
                      <Typography variant="body2">error: {themeContextValue.theme.colors.error.main}</Typography>
                    </div>
                  </div>
                </Card>
              </div>
              
              <Divider style={{ margin: '32px 0' }} />
              
              <Typography variant="h5" style={{ marginBottom: '20px' }}>
                Responsive Design
              </Typography>
              
              <Typography variant="body1" style={{ marginBottom: '24px' }}>
                All components in the Aghrba Design System are designed to be responsive and adapt to different screen sizes. The system uses a flexible grid system, responsive typography, and adaptive layouts to ensure a consistent experience across devices.
              </Typography>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
              }}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <div style={{ 
                    width: '100px', 
                    height: '180px', 
                    border: `2px solid ${themeContextValue.theme.colors.primary.main}`,
                    borderRadius: '12px',
                    margin: '0 auto 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '8px',
                    gap: '8px'
                  }}>
                    <div style={{ 
                      height: '8px', 
                      backgroundColor: themeContextValue.theme.colors.primary.main,
                      borderRadius: '4px'
                    }} />
                    <div style={{ 
                      height: '8px', 
                      width: '70%',
                      backgroundColor: themeContextValue.theme.colors.primary.main,
                      borderRadius: '4px'
                    }} />
                    <div style={{ 
                      height: '50px', 
                      backgroundColor: themeContextValue.theme.colors.secondary.main,
                      borderRadius: '6px'
                    }} />
                    <div style={{ 
                      height: '8px', 
                      width: '50%',
                      backgroundColor: themeContextValue.theme.colors.primary.main,
                      borderRadius: '4px'
                    }} />
                  </div>
                  <Typography variant="body2">Mobile</Typography>
                </div>
                
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <div style={{ 
                    width: '150px', 
                    height: '180px', 
                    border: `2px solid ${themeContextValue.theme.colors.primary.main}`,
                    borderRadius: '8px',
                    margin: '0 auto 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '10px',
                    gap: '10px'
                  }}>
                    <div style={{ 
                      height: '10px', 
                      backgroundColor: themeContextValue.theme.colors.primary.main,
                      borderRadius: '4px'
                    }} />
                    <div style={{ 
                      height: '10px', 
                      width: '70%',
                      backgroundColor: themeContextValue.theme.colors.primary.main,
                      borderRadius: '4px'
                    }} />
                    <div style={{ 
                      height: '50px', 
                      display: 'flex',
                      gap: '8px'
                    }}>
                      <div style={{ 
                        height: '50px', 
                        width: '50%',
                        backgroundColor: themeContextValue.theme.colors.secondary.main,
                        borderRadius: '6px'
                      }} />
                      <div style={{ 
                        height: '50px', 
                        width: '50%',
                        backgroundColor: themeContextValue.theme.colors.secondary.main,
                        borderRadius: '6px'
                      }} />
                    </div>
                    <div style={{ 
                      height: '10px', 
                      width: '50%',
                      backgroundColor: themeContextValue.theme.colors.primary.main,
                      borderRadius: '4px'
                    }} />
                  </div>
                  <Typography variant="body2">Tablet</Typography>
                </div>
                
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <div style={{ 
                    width: '180px', 
                    height: '180px', 
                    border: `2px solid ${themeContextValue.theme.colors.primary.main}`,
                    borderRadius: '6px',
                    margin: '0 auto 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '12px',
                    gap: '12px'
                  }}>
                    <div style={{ 
                      height: '12px', 
                      backgroundColor: themeContextValue.theme.colors.primary.main,
                      borderRadius: '4px'
                    }} />
                    <div style={{ 
                      height: '12px', 
                      width: '70%',
                      backgroundColor: themeContextValue.theme.colors.primary.main,
                      borderRadius: '4px'
                    }} />
                    <div style={{ 
                      height: '50px', 
                      display: 'flex',
                      gap: '10px'
                    }}>
                      <div style={{ 
                        height: '50px', 
                        width: '33.3%',
                        backgroundColor: themeContextValue.theme.colors.secondary.main,
                        borderRadius: '6px'
                      }} />
                      <div style={{ 
                        height: '50px', 
                        width: '33.3%',
                        backgroundColor: themeContextValue.theme.colors.secondary.main,
                        borderRadius: '6px'
                      }} />
                      <div style={{ 
                        height: '50px', 
                        width: '33.3%',
                        backgroundColor: themeContextValue.theme.colors.secondary.main,
                        borderRadius: '6px'
                      }} />
                    </div>
                    <div style={{ 
                      height: '12px', 
                      width: '50%',
                      backgroundColor: themeContextValue.theme.colors.primary.main,
                      borderRadius: '4px'
                    }} />
                  </div>
                  <Typography variant="body2">Desktop</Typography>
                </div>
              </div>
            </Card>
          </div>
        );
        
      case 'components':
        return (
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <Card style={{ padding: '30px', marginBottom: '32px' }}>
              <Typography variant="h4" style={{ marginBottom: '24px' }}>
                Basic Components
              </Typography>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '32px' }}>
                <div>
                  <Typography variant="h6" style={{ marginBottom: '16px' }}>Buttons</Typography>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                    <Button>Primary</Button>
                    <Button color="secondary">Secondary</Button>
                    <Button variant="outlined">Outlined</Button>
                    <Button variant="text">Text</Button>
                    <Button isGlowing>Magic</Button>
                  </div>
                  
                  <Typography variant="h6" style={{ marginBottom: '16px', marginTop: '24px' }}>Icon Buttons</Typography>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                    <IconButton><MockIcons.Add /></IconButton>
                    <IconButton color="secondary"><MockIcons.Search /></IconButton>
                    <IconButton variant="outlined"><MockIcons.Globe /></IconButton>
                    <ThemeToggle />
                  </div>
                </div>
                
                <div>
                  <Typography variant="h6" style={{ marginBottom: '16px' }}>Form Inputs</Typography>
                  <TextField 
                    label="Text Field"
                    placeholder="Enter some text..."
                    style={{ marginBottom: '16px' }}
                  />
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                    <Checkbox />
                    <Typography variant="body2">Checkbox</Typography>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                    <Radio />
                    <Typography variant="body2">Radio Button</Typography>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Switch />
                    <Typography variant="body2">Toggle Switch</Typography>
                  </div>
                </div>
                
                <div>
                  <Typography variant="h6" style={{ marginBottom: '16px' }}>Badges & Chips</Typography>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                    <Badge>Default</Badge>
                    <Badge color="secondary">Secondary</Badge>
                    <Badge color="success">Success</Badge>
                    <Badge color="error">Error</Badge>
                  </div>
                  
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                    <Badge variant="outlined">Outlined</Badge>
                    <Badge variant="soft" color="secondary">Soft</Badge>
                  </div>
                  
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    <Chip label="Basic Chip" />
                    <Chip label="Secondary" color="secondary" />
                    <Chip label="Deletable" onDelete={() => {}} />
                    <Chip 
                      label="With Avatar" 
                      avatar={<Avatar size="small">A</Avatar>} 
                      color="secondary"
                    />
                  </div>
                </div>
                
                <div>
                  <Typography variant="h6" style={{ marginBottom: '16px' }}>Avatars</Typography>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
                    <Avatar>JS</Avatar>
                    <Avatar color="secondary">AS</Avatar>
                    <Avatar status="online">MJ</Avatar>
                    <Avatar status="offline">DB</Avatar>
                  </div>
                  
                  <Typography variant="h6" style={{ marginBottom: '16px', marginTop: '24px' }}>Avatar Groups</Typography>
                  <AvatarGroup>
                    <Avatar>JS</Avatar>
                    <Avatar color="secondary">AS</Avatar>
                    <Avatar>MJ</Avatar>
                    <Avatar>DB</Avatar>
                    <Avatar>TK</Avatar>
                    <Avatar>WL</Avatar>
                  </AvatarGroup>
                </div>
              </div>
            </Card>
            
            <Card style={{ padding: '30px', marginBottom: '32px' }}>
              <Typography variant="h4" style={{ marginBottom: '24px' }}>
                Cards & Progress
              </Typography>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                <Card isGlowing>
                  <div style={{ padding: '20px' }}>
                    <Typography variant="h6" style={{ marginBottom: '8px' }}>
                      Glowing Card
                    </Typography>
                    <Typography variant="body2" style={{ marginBottom: '16px' }}>
                      This card has a subtle magical glow effect that highlights important content.
                    </Typography>
                    <Button color="secondary" size="small">
                      Action
                    </Button>
                  </div>
                </Card>
                
                <Card gradientBorder>
                  <div style={{ padding: '20px' }}>
                    <Typography variant="h6" style={{ marginBottom: '8px' }}>
                      Gradient Border Card
                    </Typography>
                    <Typography variant="body2" style={{ marginBottom: '16px' }}>
                      This card has an animated gradient border to add a magical touch.
                    </Typography>
                    <Button variant="outlined" color="secondary" size="small">
                      Action
                    </Button>
                  </div>
                </Card>
                
                <Card isFloating>
                  <div style={{ padding: '20px' }}>
                    <Typography variant="h6" style={{ marginBottom: '8px' }}>
                      Floating Card
                    </Typography>
                    <Typography variant="body2" style={{ marginBottom: '16px' }}>
                      This card gently floats up and down with a smooth animation.
                    </Typography>
                    <Button variant="text" color="secondary" size="small">
                      Action
                    </Button>
                  </div>
                </Card>
              </div>
              
              <Typography variant="h5" style={{ marginBottom: '20px' }}>
                Progress Indicators
              </Typography>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '24px' }}>
                <div>
                  <Typography variant="subtitle1" style={{ marginBottom: '12px' }}>
                    Progress Bars
                  </Typography>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ marginBottom: '4px' }}>
                      Primary (45%)
                    </Typography>
                    <ProgressBar value={45} />
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ marginBottom: '4px' }}>
                      Secondary (65%)
                    </Typography>
                    <ProgressBar value={65} color="secondary" />
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ marginBottom: '4px' }}>
                      Success (80%)
                    </Typography>
                    <ProgressBar value={80} color="success" />
                  </div>
                  
                  <div>
                    <Typography variant="body2" style={{ marginBottom: '4px' }}>
                      Indeterminate
                    </Typography>
                    <ProgressBar variant="indeterminate" />
                  </div>
                </div>
              </div>
            </Card>
            
            <Card style={{ padding: '30px' }}>
              <Typography variant="h4" style={{ marginBottom: '24px' }}>
                Listing Cards
              </Typography>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '24px' }}>
                {sampleProjects.map((project) => (
                  <ListingCard
                    key={project.id}
                    title={project.title}
                    subtitle={`Project ID: ${project.id}`}
                    description={project.description}
                    tags={project.tags}
                    status={project.status}
                    date={project.date}
                    progress={project.progress}
                    actions={
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <IconButton size="small" color="secondary">
                          <MockIcons.Edit size={16} />
                        </IconButton>
                        <IconButton size="small" color="error">
                          <MockIcons.Delete size={16} />
                        </IconButton>
                      </div>
                    }
                  />
                ))}
              </div>
            </Card>
          </div>
        );
        
      case 'tableView':
        return (
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <Card style={{ padding: '30px' }}>
              <Typography variant="h4" style={{ marginBottom: '24px' }}>
                Table View
              </Typography>
              
              <Typography variant="body1" style={{ marginBottom: '24px' }}>
                Tables provide a way to organize and display data in rows and columns. The Aghrba Design System includes a comprehensive table component with features like sorting, pagination, and row selection.
              </Typography>
              
              <Table 
                columns={sampleTableColumns} 
                data={sampleTableData}
                pagination
                selectable
                selectedRows={[1, 3]}
                page={1}
                rowsPerPage={5}
              />
            </Card>
          </div>
        );
        
      case 'gridView':
        return (
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <Card style={{ padding: '30px' }}>
              <Typography variant="h4" style={{ marginBottom: '24px' }}>
                Grid View
              </Typography>
              
              <Typography variant="body1" style={{ marginBottom: '24px' }}>
                Grid views provide a way to display collections of items in a responsive grid layout. This is useful for galleries, product listings, dashboards, and more.
              </Typography>
              
              <GridView
                items={sampleProjects}
                renderItem={(item) => (
                  <ListingCard
                    title={item.title}
                    description={item.description}
                    tags={item.tags}
                    status={item.status}
                    progress={item.progress}
                    variant="compact"
                  />
                )}
                columns={{ xs: 1, sm: 2, md: 3, lg: 4 }}
                spacing={16}
              />
            </Card>
          </div>
        );
        
      case 'kanbanView':
        return (
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <Card style={{ padding: '30px' }}>
              <Typography variant="h4" style={{ marginBottom: '24px' }}>
                Kanban Board
              </Typography>
              
              <Typography variant="body1" style={{ marginBottom: '24px' }}>
                Kanban boards provide a visual way to manage work and workflow. Items are represented as cards which can be moved between columns representing different stages of the workflow.
              </Typography>
              
              <KanbanBoard
                columns={sampleKanbanColumns}
              />
            </Card>
          </div>
        );
        
      case 'calendar':
        return (
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <Card style={{ padding: '30px' }}>
              <Typography variant="h4" style={{ marginBottom: '24px' }}>
                Calendar View
              </Typography>
              
              <Typography variant="body1" style={{ marginBottom: '24px' }}>
                The calendar component provides different views (month, week, day) to display and manage events and appointments.
              </Typography>
              
              <CalendarView
                events={sampleCalendarEvents}
                date={new Date(2025, 4, 3)}
                view="month"
              />
            </Card>
          </div>
        );
        
      case 'team':
        return (
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <Card style={{ padding: '30px' }}>
              <Typography variant="h4" style={{ marginBottom: '24px' }}>
                Team Management
              </Typography>
              
              <Typography variant="body1" style={{ marginBottom: '24px' }}>
                The team management component provides a way to view and manage team members, their roles, workload, and assigned projects.
              </Typography>
              
              <TeamManagementView
                members={sampleTeamMembers}
                projects={sampleTeamProjects}
              />
            </Card>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <ThemeContext.Provider value={themeContextValue}>
      <div style={containerStyle}>
        {/* Header */}
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '0 20px',
          marginBottom: '40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h3" style={{
            fontFamily: themeContextValue.theme.typography.fontFamily.display,
            ...themeContextValue.theme.styles.gradientText
          }}>
            Aghrba Design System
          </Typography>
          
          <ThemeToggle size="large" />
        </div>
        
        {/* Navigation Tabs */}
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '0 20px',
          marginBottom: '40px'
        }}>
          <Card style={{ marginBottom: '30px', overflow: 'hidden' }}>
            <div style={{ 
              borderBottom: `1px solid ${themeContextValue.theme.colors.divider}`,
              display: 'flex',
              position: 'relative',
              overflowX: 'auto',
              ...themeContextValue.theme.styles.scrollbarStyles
            }}>
              {tabs.map((tab, index) => (
                <div
                  key={index}
                  onClick={() => setActiveTab(index)}
                  style={{
                    padding: '16px 24px',
                    cursor: 'pointer',
                    color: activeTab === index ? themeContextValue.theme.colors.text.primary : themeContextValue.theme.colors.text.secondary,
                    fontWeight: activeTab === index ? 600 : 400,
                    transition: 'color 0.3s',
                    position: 'relative',
                    flexShrink: 0,
                    textAlign: 'center',
                    '&:hover': {
                      color: activeTab === index ? themeContextValue.theme.colors.text.primary : themeContextValue.theme.colors.text.accent,
                    }
                  }}
                >
                  {tab.label}
                </div>
              ))}
              <div style={{ 
                position: 'absolute',
                bottom: 0,
                left: `${tabs[activeTab].label.length * 2 + activeTab * 48}px`,
                width: `${tabs[activeTab].label.length * 10}px`,
                height: '2px',
                backgroundColor: themeContextValue.theme.colors.secondary.main,
                transition: 'left 0.3s ease, width 0.3s ease',
                borderRadius: '2px 2px 0 0',
              }} />
            </div>
          </Card>
        </div>
        
        {/* Tab Content */}
        {renderTabContent()}
        
        {/* Footer */}
        <div style={{ 
          maxWidth: '1200px', 
          margin: '60px auto 0',
          padding: '20px',
          textAlign: 'center',
          color: themeContextValue.theme.colors.text.secondary,
          fontSize: '0.875rem',
          borderTop: `1px solid ${themeContextValue.theme.colors.divider}`
        }}>
          Aghrba Design System • {new Date().getFullYear()}
        </div>
      </div>
    </ThemeContext.Provider>
  );
};

// Hook to use theme context
const useTheme = () => {
  return React.useContext(ThemeContext);
};

const TextField = ({ 
  label, 
  placeholder,
  value,
  onChange,
  type = 'text',
  size = 'medium',
  fullWidth = false,
  error = false,
  helperText = '',
  startIcon,
  endIcon,
  multiline = false,
  rows = 3,
  required = false,
  disabled = false,
  style,
  ...props 
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  const [focused, setFocused] = useState(false);
  
  const getSizeStyles = () => {
    switch(size) {
      case 'small':
        return { 
          padding: '8px 12px',
          fontSize: '0.875rem',
        };
      case 'large':
        return { 
          padding: '12px 16px',
          fontSize: '1rem',
        };
      default: // medium
        return { 
          padding: '10px 14px',
          fontSize: '0.875rem',
        };
    }
  };
  
  const containerStyles = {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '1rem',
    width: fullWidth ? '100%' : 'auto',
    ...style
  };
  
  const labelStyles = {
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: '0.875rem',
    color: error ? currentTheme.colors.error.main : currentTheme.colors.text.secondary,
    marginBottom: '0.5rem',
    display: 'flex',
    alignItems: 'center'
  };
  
  const inputWrapperStyles = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%'
  };
  
  const inputStyles = {
    fontFamily: currentTheme.typography.fontFamily.primary,
    color: disabled ? currentTheme.colors.text.disabled : currentTheme.colors.text.primary,
    backgroundColor: disabled ? `${currentTheme.colors.background.light}80` : currentTheme.colors.background.light,
    border: error ? 
      `1px solid ${currentTheme.colors.error.main}` :
      focused ? 
        `1px solid ${currentTheme.colors.secondary.main}` : 
        '1px solid transparent',
    borderRadius: currentTheme.borderRadius.pill,
    transition: 'all 0.3s ease',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    paddingLeft: startIcon ? '36px' : getSizeStyles().padding,
    paddingRight: endIcon ? '36px' : getSizeStyles().padding,
    paddingTop: getSizeStyles().padding,
    paddingBottom: getSizeStyles().padding,
    fontSize: getSizeStyles().fontSize,
    opacity: disabled ? 0.7 : 1,
    cursor: disabled ? 'not-allowed' : 'text',
    resize: multiline ? 'vertical' : 'none',
    minHeight: multiline ? `${rows * 24}px` : 'auto',
    '&::placeholder': {
      color: currentTheme.colors.text.disabled,
    },
    '&:hover': {
      backgroundColor: disabled ? `${currentTheme.colors.background.light}80` : currentTheme.colors.background.light,
    },
    '&:focus': {
      border: error ? 
        `1px solid ${currentTheme.colors.error.main}` :
        `1px solid ${currentTheme.colors.secondary.main}`,
      boxShadow: error ? 
        `0 0 0 3px ${currentTheme.colors.error.main}20` :
        `0 0 0 3px ${currentTheme.colors.secondary.main}40`,
    }
  };
  
  const iconStyles = {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: currentTheme.colors.text.secondary,
    pointerEvents: 'none'
  };
  
  const helperTextStyles = {
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: '0.75rem',
    color: error ? currentTheme.colors.error.main : currentTheme.colors.text.secondary,
    marginTop: '0.25rem',
    minHeight: '1rem'
  };
  
  const requiredMark = required ? (
    <span style={{ color: currentTheme.colors.error.main, marginLeft: '4px' }}>*</span>
  ) : null;

  const InputComponent = multiline ? 'textarea' : 'input';
  
  return (
    <div style={containerStyles}>
      {label && (
        <label style={labelStyles}>
          {label}{requiredMark}
        </label>
      )}
      <div style={inputWrapperStyles}>
        {startIcon && (
          <div style={{ ...iconStyles, left: '12px' }}>
            {startIcon}
          </div>
        )}
        <InputComponent
          type={multiline ? undefined : type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          style={inputStyles}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={disabled}
          rows={multiline ? rows : undefined}
          {...props}
        />
        {endIcon && (
          <div style={{ ...iconStyles, right: '12px' }}>
            {endIcon}
          </div>
        )}
      </div>
      <div style={helperTextStyles}>
        {helperText}
      </div>
    </div>
  );
};

const Badge = ({
  color = 'primary',
  variant = 'filled',
  size = 'medium',
  children,
  style,
  ...props
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  
  // Get color based on the color prop
  const getColor = () => {
    const colors = {
      primary: currentTheme.colors.primary,
      secondary: currentTheme.colors.secondary,
      success: currentTheme.colors.success,
      error: currentTheme.colors.error,
      warning: currentTheme.colors.warning,
      info: currentTheme.colors.info,
    };
    
    return colors[color] || colors.primary;
  };
  
  // Get size styles
  const getSizeStyles = () => {
    switch(size) {
      case 'small':
        return {
          padding: '2px 8px',
          fontSize: '0.65rem',
          fontWeight: 500
        };
      case 'large':
        return {
          padding: '6px 16px',
          fontSize: '0.85rem',
          fontWeight: 600
        };
      default: // medium
        return {
          padding: '4px 12px',
          fontSize: '0.75rem',
          fontWeight: 600
        };
    }
  };
  
  // Get variant styles
  const getVariantStyles = () => {
    const selectedColor = getColor();
    
    switch(variant) {
      case 'filled':
        return {
          backgroundColor: selectedColor.main,
          color: selectedColor.contrastText,
          border: 'none'
        };
      case 'outlined':
        return {
          backgroundColor: 'transparent',
          color: selectedColor.main,
          border: `1px solid ${selectedColor.main}`
        };
      case 'soft':
        return {
          backgroundColor: `${selectedColor.main}20`,
          color: selectedColor.main,
          border: 'none'
        };
      default:
        return {};
    }
  };
  
  const badgeStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: currentTheme.borderRadius.pill,
    fontFamily: currentTheme.typography.fontFamily.primary,
    lineHeight: 1.2,
    ...getSizeStyles(),
    ...getVariantStyles(),
    ...style
  };
  
  return (
    <span style={badgeStyles} {...props}>
      {children}
    </span>
  );
};

const Avatar = ({
  src,
  alt = '',
  size = 'medium',
  color = 'primary',
  children,
  status,
  style,
  ...props
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  
  // Get size in pixels
  const getSizeInPx = () => {
    switch(size) {
      case 'tiny': return 24;
      case 'small': return 32;
      case 'medium': return 40;
      case 'large': return 48;
      case 'xlarge': return 64;
      default: return 40;
    }
  };
  
  // Get color based on the color prop
  const getColorStyles = () => {
    if (src) return {}; // No background color needed for image avatars
    
    const colors = {
      primary: {
        backgroundColor: currentTheme.colors.primary.main,
        color: currentTheme.colors.primary.contrastText
      },
      secondary: {
        backgroundColor: currentTheme.colors.secondary.main,
        color: currentTheme.colors.secondary.contrastText
      },
      success: {
        backgroundColor: currentTheme.colors.success.main,
        color: currentTheme.colors.success.contrastText
      },
      error: {
        backgroundColor: currentTheme.colors.error.main,
        color: currentTheme.colors.error.contrastText
      },
      warning: {
        backgroundColor: currentTheme.colors.warning.main,
        color: currentTheme.colors.warning.contrastText
      },
      info: {
        backgroundColor: currentTheme.colors.info.main,
        color: currentTheme.colors.info.contrastText
      },
    };
    
    return colors[color] || colors.primary;
  };
  
  // Get status styles
  const getStatusStyles = () => {
    if (!status) return null;
    
    const sizeInPx = getSizeInPx();
    const statusColors = {
      online: currentTheme.colors.success.main,
      offline: currentTheme.colors.text.disabled,
      busy: currentTheme.colors.error.main,
      away: currentTheme.colors.warning.main
    };
    
    const statusColor = statusColors[status] || statusColors.offline;
    const statusSize = sizeInPx * 0.25; // Status indicator is 25% of avatar size
    
    return {
      position: 'absolute',
      width: statusSize,
      height: statusSize,
      borderRadius: '50%',
      backgroundColor: statusColor,
      border: `2px solid ${currentTheme.colors.background.default}`,
      bottom: 0,
      right: 0
    };
  };
  
  const sizeInPx = getSizeInPx();
  
  const avatarStyles = {
    position: 'relative',
    width: sizeInPx,
    height: sizeInPx,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: sizeInPx * 0.4, // Font size is 40% of avatar size
    fontWeight: 600,
    overflow: 'hidden',
    ...getColorStyles(),
    ...style
  };
  
  return (
    <div style={avatarStyles} {...props}>
      {src ? (
        <img 
          src={src} 
          alt={alt} 
          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
        />
      ) : (
        children
      )}
      {status && <div style={getStatusStyles()} />}
    </div>
  );
};

const AvatarGroup = ({
  children,
  max = 5,
  spacing = -8,
  size = 'medium',
  style,
  ...props
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  
  const avatars = React.Children.toArray(children);
  const totalAvatars = avatars.length;
  const displayedAvatars = max ? avatars.slice(0, max) : avatars;
  const remainingAvatars = max && totalAvatars > max ? totalAvatars - max : 0;
  
  const avatarGroupStyles = {
    display: 'flex',
    alignItems: 'center',
    ...style
  };
  
  return (
    <div style={avatarGroupStyles} {...props}>
      {displayedAvatars.map((avatar, index) => (
        <div 
          key={index} 
          style={{ 
            marginLeft: index === 0 ? 0 : spacing,
            zIndex: displayedAvatars.length - index,
            position: 'relative',
            // If we're using border, add a border to create separation
            outline: `2px solid ${currentTheme.colors.background.default}`,
            borderRadius: '50%'
          }}
        >
          {React.cloneElement(avatar, { size })}
        </div>
      ))}
      
      {remainingAvatars > 0 && (
        <Avatar 
          size={size} 
          color="primary"
          style={{ 
            marginLeft: spacing,
            zIndex: 0,
            outline: `2px solid ${currentTheme.colors.background.default}`,
          }}
        >
          +{remainingAvatars}
        </Avatar>
      )}
    </div>
  );
};

const Chip = ({
  label,
  color = 'primary',
  variant = 'filled',
  size = 'medium',
  avatar,
  deleteIcon,
  onDelete,
  disabled = false,
  style,
  ...props
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  
  // Get color based on the color prop
  const getColor = () => {
    const colors = {
      primary: currentTheme.colors.primary,
      secondary: currentTheme.colors.secondary,
      success: currentTheme.colors.success,
      error: currentTheme.colors.error,
      warning: currentTheme.colors.warning,
      info: currentTheme.colors.info,
    };
    
    return colors[color] || colors.primary;
  };
  
  // Get size styles
  const getSizeStyles = () => {
    switch(size) {
      case 'small':
        return {
          height: '24px',
          fontSize: '0.75rem',
          padding: '0 8px'
        };
      case 'large':
        return {
          height: '36px',
          fontSize: '0.875rem',
          padding: '0 14px'
        };
      default: // medium
        return {
          height: '32px',
          fontSize: '0.8125rem',
          padding: '0 12px'
        };
    }
  };
  
  // Get variant styles
  const getVariantStyles = () => {
    const selectedColor = getColor();
    
    switch(variant) {
      case 'filled':
        return {
          backgroundColor: disabled ? currentTheme.colors.text.disabled : selectedColor.main,
          color: disabled ? currentTheme.colors.background.paper : selectedColor.contrastText,
          border: 'none'
        };
      case 'outlined':
        return {
          backgroundColor: 'transparent',
          color: disabled ? currentTheme.colors.text.disabled : selectedColor.main,
          border: `1px solid ${disabled ? currentTheme.colors.text.disabled : selectedColor.main}`
        };
      case 'soft':
        return {
          backgroundColor: disabled ? `${currentTheme.colors.text.disabled}20` : `${selectedColor.main}20`,
          color: disabled ? currentTheme.colors.text.disabled : selectedColor.main,
          border: 'none'
        };
      default:
        return {};
    }
  };
  
  // Get disabled styles
  const getDisabledStyles = () => {
    if (!disabled) return {};
    
    return {
      opacity: 0.7,
      cursor: 'default',
    };
  };
  
  const sizeStyles = getSizeStyles();
  
  const chipStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: currentTheme.borderRadius.pill,
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontWeight: 500,
    margin: '4px',
    cursor: disabled ? 'default' : 'default',
    transition: 'all 0.2s ease',
    ...sizeStyles,
    ...getVariantStyles(),
    ...getDisabledStyles(),
    ...style
  };
  
  // Adjust padding if there's an avatar
  if (avatar) {
    chipStyles.paddingLeft = '4px';
  }
  
  // Adjust height for the delete icon
  const deleteIconSize = {
    small: 16,
    medium: 18,
    large: 20
  }[size];
  
  const deleteButtonStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '4px',
    marginRight: '-4px',
    width: deleteIconSize + 8,
    height: deleteIconSize + 8,
    borderRadius: '50%',
    cursor: disabled ? 'default' : 'pointer',
    color: 'inherit',
    opacity: 0.7,
    transition: 'all 0.2s ease',
    backgroundColor: 'transparent',
    border: 'none',
    padding: 0,
    '&:hover': !disabled && {
      opacity: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.1)'
    }
  };
  
  return (
    <div style={chipStyles} {...props}>
      {avatar && (
        <div style={{ marginRight: '4px' }}>
          {avatar}
        </div>
      )}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      {onDelete && !disabled && (
        <button 
          onClick={onDelete}
          style={deleteButtonStyles}
          aria-label="Remove"
        >
          {deleteIcon || <MockIcons.Delete size={deleteIconSize} />}
        </button>
      )}
    </div>
  );
};

const Divider = ({
  orientation = 'horizontal',
  variant = 'fullWidth',
  light = false,
  spacing = 2,
  style,
  ...props
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  
  const getOrientationStyles = () => {
    return orientation === 'vertical' 
      ? { 
          height: '100%', 
          width: '1px',
          minWidth: '1px',
          margin: `0 ${spacing * 8}px`
        } 
      : { 
          width: '100%', 
          height: '1px',
          minHeight: '1px',
          margin: `${spacing * 8}px 0` 
        };
  };
  
  const getVariantStyles = () => {
    switch(variant) {
      case 'inset':
        return orientation === 'vertical' 
          ? { marginTop: '8px', marginBottom: '8px' } 
          : { marginLeft: '72px' };
      case 'middle':
        return orientation === 'vertical' 
          ? { marginTop: '8px', marginBottom: '8px' } 
          : { marginLeft: '16px', marginRight: '16px' };
      default:
        return {};
    }
  };
  
  const dividerStyles = {
    backgroundColor: light 
      ? `${currentTheme.colors.divider}40` 
      : currentTheme.colors.divider,
    flexShrink: 0,
    ...getOrientationStyles(),
    ...getVariantStyles(),
    ...style
  };
  
  return <div style={dividerStyles} role="separator" {...props} />;
};

const ProgressBar = ({
  variant = 'determinate',
  value = 0,
  color = 'primary',
  size = 'medium',
  label = false,
  style,
  ...props
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  
  // Get color based on the color prop
  const getColor = () => {
    const colors = {
      primary: currentTheme.colors.primary.main,
      secondary: currentTheme.colors.secondary.main,
      success: currentTheme.colors.success.main,
      error: currentTheme.colors.error.main,
      warning: currentTheme.colors.warning.main,
      info: currentTheme.colors.info.main,
    };
    
    return colors[color] || colors.primary;
  };
  
  // Get size styles
  const getSizeStyles = () => {
    switch(size) {
      case 'small':
        return { height: '4px' };
      case 'large':
        return { height: '8px' };
      default: // medium
        return { height: '6px' };
    }
  };
  
  // For indeterminate progress bar
  const createIndeterminateAnimation = () => {
    return {
      animation: 'indeterminate 1.5s ease-in-out infinite',
      '@keyframes indeterminate': {
        '0%': { left: '-35%', right: '100%' },
        '60%': { left: '100%', right: '-90%' },
        '100%': { left: '100%', right: '-90%' }
      }
    };
  };
  
  const containerStyles = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    ...style
  };
  
  const barContainerStyles = {
    position: 'relative',
    width: '100%',
    borderRadius: currentTheme.borderRadius.pill,
    backgroundColor: `${getColor()}30`,
    overflow: 'hidden',
    ...getSizeStyles()
  };
  
  const barStyles = {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    backgroundColor: getColor(),
    borderRadius: 'inherit',
    transition: 'width 0.4s ease',
    ...(variant === 'determinate' 
      ? { width: `${Math.max(0, Math.min(100, value))}%` } 
      : createIndeterminateAnimation()
    ),
  };
  
  const valueStyles = {
    marginLeft: '8px',
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: '0.75rem',
    color: currentTheme.colors.text.secondary
  };
  
  return (
    <div style={containerStyles} {...props}>
      <div style={barContainerStyles}>
        <div style={barStyles} />
      </div>
      {label && variant === 'determinate' && (
        <div style={valueStyles}>{value}%</div>
      )}
    </div>
  );
};

const Switch = ({
  checked = false,
  onChange,
  disabled = false,
  color = 'primary',
  size = 'medium',
  style,
  ...props
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  
  // Get color based on the color prop
  const getColor = () => {
    const colors = {
      primary: currentTheme.colors.primary.main,
      secondary: currentTheme.colors.secondary.main,
      success: currentTheme.colors.success.main,
      error: currentTheme.colors.error.main,
      warning: currentTheme.colors.warning.main,
      info: currentTheme.colors.info.main,
    };
    
    return colors[color] || colors.primary;
  };
  
  // Get size styles
  const getSizeStyles = () => {
    switch(size) {
      case 'small':
        return { 
          width: '32px', 
          height: '16px',
          '&::after': {
            width: '12px',
            height: '12px'
          }
        };
      case 'large':
        return { 
          width: '56px', 
          height: '28px',
          '&::after': {
            width: '24px',
            height: '24px'
          }
        };
      default: // medium
        return { 
          width: '44px', 
          height: '22px',
          '&::after': {
            width: '18px',
            height: '18px'
          }
        };
    }
  };
  
  // Get disabled styles
  const getDisabledStyles = () => {
    if (!disabled) return {};
    
    return {
      opacity: 0.5,
      cursor: 'not-allowed',
      pointerEvents: 'none'
    };
  };
  
  const sizeStyles = getSizeStyles();
  
  const switchStyles = {
    position: 'relative',
    display: 'inline-block',
    borderRadius: currentTheme.borderRadius.pill,
    backgroundColor: checked ? getColor() : currentTheme.colors.text.disabled,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.3s ease',
    ...sizeStyles,
    ...getDisabledStyles(),
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      transform: `translateY(-50%) translateX(${checked ? 'calc(100% - 2px)' : '2px'})`,
      backgroundColor: '#ffffff',
      borderRadius: '50%',
      transition: 'transform 0.3s ease',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
      ...sizeStyles['&::after']
    },
    ...style
  };
  
  const handleChange = (e) => {
    if (disabled) return;
    if (onChange) onChange(e);
  };
  
  return (
    <div 
      style={switchStyles}
      onClick={handleChange}
      role="switch"
      aria-checked={checked}
      {...props}
    />
  );
};

const Checkbox = ({
  checked = false,
  onChange,
  disabled = false,
  color = 'primary',
  size = 'medium',
  style,
  ...props
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  
  // Get color based on the color prop
  const getColor = () => {
    const colors = {
      primary: currentTheme.colors.primary.main,
      secondary: currentTheme.colors.secondary.main,
      success: currentTheme.colors.success.main,
      error: currentTheme.colors.error.main,
      warning: currentTheme.colors.warning.main,
      info: currentTheme.colors.info.main,
    };
    
    return colors[color] || colors.primary;
  };
  
  // Get size in pixels
  const getSizeInPx = () => {
    switch(size) {
      case 'small': return 16;
      case 'large': return 24;
      default: return 20; // medium
    }
  };
  
  // Get disabled styles
  const getDisabledStyles = () => {
    if (!disabled) return {};
    
    return {
      opacity: 0.5,
      cursor: 'not-allowed',
      pointerEvents: 'none'
    };
  };
  
  const sizeInPx = getSizeInPx();
  
  const checkboxStyles = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: sizeInPx,
    height: sizeInPx,
    borderRadius: currentTheme.borderRadius.sm,
    backgroundColor: checked ? getColor() : 'transparent',
    border: checked ? 'none' : `2px solid ${disabled ? currentTheme.colors.text.disabled : currentTheme.colors.text.secondary}`,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    ...getDisabledStyles(),
    ...style
  };
  
  const checkMarkStyles = {
    color: '#ffffff',
    display: checked ? 'block' : 'none'
  };
  
  const handleChange = (e) => {
    if (disabled) return;
    if (onChange) onChange(e);
  };
  
  return (
    <div 
      style={checkboxStyles}
      onClick={handleChange}
      role="checkbox"
      aria-checked={checked}
      {...props}
    >
      <div style={checkMarkStyles}>
        <MockIcons.Check size={sizeInPx * 0.8} color="#ffffff" />
      </div>
    </div>
  );
};

const Radio = ({
  checked = false,
  onChange,
  disabled = false,
  color = 'primary',
  size = 'medium',
  style,
  ...props
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  
  // Get color based on the color prop
  const getColor = () => {
    const colors = {
      primary: currentTheme.colors.primary.main,
      secondary: currentTheme.colors.secondary.main,
      success: currentTheme.colors.success.main,
      error: currentTheme.colors.error.main,
      warning: currentTheme.colors.warning.main,
      info: currentTheme.colors.info.main,
    };
    
    return colors[color] || colors.primary;
  };
  
  // Get size in pixels
  const getSizeInPx = () => {
    switch(size) {
      case 'small': return 16;
      case 'large': return 24;
      default: return 20; // medium
    }
  };
  
  // Get disabled styles
  const getDisabledStyles = () => {
    if (!disabled) return {};
    
    return {
      opacity: 0.5,
      cursor: 'not-allowed',
      pointerEvents: 'none'
    };
  };
  
  const sizeInPx = getSizeInPx();
  
  const radioStyles = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: sizeInPx,
    height: sizeInPx,
    borderRadius: '50%',
    backgroundColor: 'transparent',
    border: `2px solid ${checked ? getColor() : (disabled ? currentTheme.colors.text.disabled : currentTheme.colors.text.secondary)}`,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'border-color 0.2s ease',
    ...getDisabledStyles(),
    ...style
  };
  
  const innerCircleStyles = {
    width: sizeInPx * 0.5,
    height: sizeInPx * 0.5,
    borderRadius: '50%',
    backgroundColor: getColor(),
    opacity: checked ? 1 : 0,
    transform: checked ? 'scale(1)' : 'scale(0)',
    transition: 'transform 0.2s ease, opacity 0.2s ease'
  };
  
  const handleChange = (e) => {
    if (disabled) return;
    if (onChange) onChange(e);
  };
  
  return (
    <div 
      style={radioStyles}
      onClick={handleChange}
      role="radio"
      aria-checked={checked}
      {...props}
    >
      <div style={innerCircleStyles} />
    </div>
  );
};

const ThemeToggle = ({
  size = 'medium',
  style,
  ...props
}) => {
  const { themeMode, toggleTheme, theme: currentTheme = AghrbaThemes.dark } = useTheme();
  
  const getSizeStyles = () => {
    switch(size) {
      case 'small':
        return { 
          padding: '6px',
          width: '28px',
          height: '28px',
          iconSize: 16
        };
      case 'large':
        return { 
          padding: '12px',
          width: '48px',
          height: '48px',
          iconSize: 24
        };
      default: // medium
        return { 
          padding: '8px',
          width: '36px',
          height: '36px',
          iconSize: 20
        };
    }
  };
  
  const { iconSize, ...sizeStyles } = getSizeStyles();
  
  const toggleStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: currentTheme.colors.background.light,
    color: themeMode === 'dark' ? currentTheme.colors.secondary.main : currentTheme.colors.primary.main,
    cursor: 'pointer',
    transition: 'background-color 0.3s ease, color 0.3s ease',
    border: 'none',
    ...sizeStyles,
    '&:hover': {
      backgroundColor: currentTheme.colors.background.lighter,
    },
    ...style
  };
  
  return (
    <button
      style={toggleStyles}
      onClick={toggleTheme}
      title={`Switch to ${themeMode === 'dark' ? 'light' : 'dark'} mode`}
      aria-label={`Switch to ${themeMode === 'dark' ? 'light' : 'dark'} mode`}
      {...props}
    >
      {themeMode === 'dark' ? (
        <MockIcons.Sun size={iconSize} />
      ) : (
        <MockIcons.Moon size={iconSize} />
      )}
    </button>
  );
};

const ListingCard = ({
  title,
  subtitle,
  description,
  image,
  tags = [],
  status,
  date,
  progress,
  actions,
  orientation = 'vertical',
  variant = 'default',
  onClick,
  style,
  ...props
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  
  // Determine card orientation styles
  const getOrientationStyles = () => {
    return orientation === 'horizontal' 
      ? { 
          display: 'flex',
          flexDirection: 'row',
        }
      : {
          display: 'flex',
          flexDirection: 'column',
        };
  };
  
  // Get variant styles
  const getVariantStyles = () => {
    switch(variant) {
      case 'compact':
        return {
          padding: '12px',
          gap: '8px',
          imageHeight: orientation === 'horizontal' ? '64px' : '120px',
          imageWidth: orientation === 'horizontal' ? '64px' : '100%'
        };
      case 'detailed':
        return {
          padding: '20px',
          gap: '16px',
          imageHeight: orientation === 'horizontal' ? '180px' : '200px',
          imageWidth: orientation === 'horizontal' ? '180px' : '100%'
        };
      default: // default
        return {
          padding: '16px',
          gap: '12px',
          imageHeight: orientation === 'horizontal' ? '120px' : '160px',
          imageWidth: orientation === 'horizontal' ? '120px' : '100%'
        };
    }
  };
  
  const { imageHeight, imageWidth, ...variantStyles } = getVariantStyles();
  
  const cardStyles = {
    backgroundColor: currentTheme.colors.background.card,
    borderRadius: currentTheme.borderRadius.lg,
    boxShadow: currentTheme.shadows.md,
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
    '&:hover': onClick && {
      transform: 'translateY(-4px)',
      boxShadow: currentTheme.shadows.lg
    },
    ...getOrientationStyles(),
    ...variantStyles,
    ...style
  };
  
  const imageStyles = {
    width: imageWidth,
    height: imageHeight,
    backgroundColor: currentTheme.colors.background.light,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderRadius: currentTheme.borderRadius.md,
    flexShrink: 0,
    ...(image && { backgroundImage: `url(${image})` })
  };
  
  const contentStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1,
    overflow: 'hidden'
  };
  
  const headerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px'
  };
  
  const titleStyles = {
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: variant === 'compact' ? '0.875rem' : '1rem',
    fontWeight: 600,
    color: currentTheme.colors.text.primary,
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  };
  
  const subtitleStyles = {
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: variant === 'compact' ? '0.75rem' : '0.875rem',
    color: currentTheme.colors.text.secondary,
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  };
  
  const descriptionStyles = {
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: variant === 'compact' ? '0.75rem' : '0.875rem',
    color: currentTheme.colors.text.secondary,
    margin: '4px 0 0 0',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: variant === 'detailed' ? 3 : 2,
    WebkitBoxOrient: 'vertical',
    lineHeight: 1.4
  };
  
  const footerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: variant === 'compact' ? '8px' : '12px'
  };
  
  const actionsStyles = {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  };
  
  const tagsStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginTop: '4px'
  };
  
  const progressBarStyles = {
    marginTop: variant === 'compact' ? '4px' : '8px'
  };
  
  const dateStyles = {
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: '0.75rem',
    color: currentTheme.colors.text.secondary
  };
  
  return (
    <div style={cardStyles} onClick={onClick} {...props}>
      {image && <div style={imageStyles} />}
      
      <div style={contentStyles}>
        <div style={headerStyles}>
          <div style={{ overflow: 'hidden' }}>
            <h3 style={titleStyles}>{title}</h3>
            {subtitle && <div style={subtitleStyles}>{subtitle}</div>}
          </div>
          
          {status && (
            <Badge 
              color={
                status === 'active' ? 'success' : 
                status === 'pending' ? 'warning' :
                status === 'completed' ? 'primary' :
                status === 'cancelled' ? 'error' : 'info'
              }
              variant="soft"
              size="small"
            >
              {status}
            </Badge>
          )}
        </div>
        
        {description && (
          <div style={descriptionStyles}>{description}</div>
        )}
        
        {tags && tags.length > 0 && (
          <div style={tagsStyles}>
            {tags.map((tag, index) => (
              <Chip 
                key={index}
                label={tag}
                size="small"
                variant="soft"
                color="secondary"
              />
            ))}
          </div>
        )}
        
        {progress !== undefined && (
          <div style={progressBarStyles}>
            <ProgressBar 
              value={progress} 
              size="small" 
              label={variant === 'detailed'}
              color={progress < 30 ? 'error' : progress < 70 ? 'warning' : 'success'}
            />
          </div>
        )}
        
        <div style={footerStyles}>
          {date && <div style={dateStyles}>{date}</div>}
          
          {actions && (
            <div style={actionsStyles}>
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Table = ({
  columns = [],
  data = [],
  onRowClick,
  selectable = false,
  onSelectChange,
  selectedRows = [],
  pagination = false,
  page = 1,
  rowsPerPage = 10,
  onPageChange,
  loading = false,
  emptyMessage = 'No data available',
  style,
  ...props
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Handle row click
  const handleRowClick = (row, index) => {
    if (onRowClick) onRowClick(row, index);
  };
  
  // Handle row selection
  const handleSelectRow = (event, rowId) => {
    event.stopPropagation();
    
    if (onSelectChange) {
      const isSelected = selectedRows.includes(rowId);
      const newSelectedRows = isSelected
        ? selectedRows.filter(id => id !== rowId)
        : [...selectedRows, rowId];
      
      onSelectChange(newSelectedRows);
    }
  };
  
  // Handle header select all
  const handleSelectAll = (event) => {
    event.stopPropagation();
    
    if (onSelectChange) {
      const allRowIds = data.map(row => row.id);
      const newSelectedRows = selectedRows.length === data.length ? [] : allRowIds;
      
      onSelectChange(newSelectedRows);
    }
  };
  
  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    
    if (sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    
    setSortConfig({ key, direction });
  };
  
  // Get sorted data
  const getSortedData = () => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };
  
  // Get paginated data
  const getPaginatedData = () => {
    const sortedData = getSortedData();
    
    if (!pagination) return sortedData;
    
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    
    return sortedData.slice(startIndex, endIndex);
  };
  
  // Handle page change
  const handlePageChange = (newPage) => {
    if (onPageChange) onPageChange(newPage);
  };
  
  const displayData = getPaginatedData();
  const totalPages = pagination ? Math.ceil(data.length / rowsPerPage) : 0;
  
  const tableStyles = {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontFamily: currentTheme.typography.fontFamily.primary,
    ...style
  };
  
  const tableHeadStyles = {
    backgroundColor: currentTheme.colors.background.light,
    textAlign: 'left'
  };
  
  const thStyles = {
    padding: '12px 16px',
    fontWeight: 600,
    fontSize: '0.875rem',
    color: currentTheme.colors.text.primary,
    position: 'relative',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${currentTheme.colors.divider}`,
    userSelect: 'none'
  };
  
  const sortableThStyles = {
    cursor: 'pointer',
    paddingRight: '24px',
    '&:hover': {
      backgroundColor: currentTheme.colors.background.lighter
    }
  };
  
  const sortIconStyles = {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    transition: 'transform 0.2s ease',
    opacity: 0.7,
    display: 'inline-flex'
  };
  
  const tdStyles = {
    padding: '12px 16px',
    fontSize: '0.875rem',
    color: currentTheme.colors.text.primary,
    borderBottom: `1px solid ${currentTheme.colors.divider}`,
    verticalAlign: 'middle'
  };
  
  const trStyles = {
    transition: 'background-color 0.2s ease',
    '&:hover': onRowClick && {
      backgroundColor: currentTheme.colors.background.subtle,
      cursor: 'pointer'
    }
  };
  
  const checkboxCellStyles = {
    width: '48px',
    textAlign: 'center'
  };
  
  const emptyStateStyles = {
    padding: '48px 0',
    textAlign: 'center',
    color: currentTheme.colors.text.secondary,
    fontSize: '0.875rem',
    borderBottom: `1px solid ${currentTheme.colors.divider}`
  };
  
  const loadingOverlayStyles = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: `${currentTheme.colors.background.default}80`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1
  };
  
  const paginationStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 0',
    fontSize: '0.875rem',
    color: currentTheme.colors.text.secondary
  };
  
  const paginationButtonsStyles = {
    display: 'flex',
    gap: '8px'
  };
  
  const paginationButtonStyle = {
    padding: '4px 8px',
    minWidth: '32px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: currentTheme.borderRadius.sm,
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'background-color 0.2s ease',
    border: 'none',
    backgroundColor: 'transparent',
    color: currentTheme.colors.text.primary,
    '&:hover': {
      backgroundColor: currentTheme.colors.background.light
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  };
  
  const activePageButtonStyle = {
    ...paginationButtonStyle,
    backgroundColor: currentTheme.colors.primary.main,
    color: currentTheme.colors.primary.contrastText,
    '&:hover': {
      backgroundColor: currentTheme.colors.primary.light
    }
  };
  
  return (
    <div style={{ position: 'relative' }}>
      {loading && (
        <div style={loadingOverlayStyles}>
          <ProgressBar variant="indeterminate" />
        </div>
      )}
      
      <div style={{ overflowX: 'auto', ...currentTheme.styles.scrollbarStyles }}>
        <table style={tableStyles} {...props}>
          <thead style={tableHeadStyles}>
            <tr>
              {selectable && (
                <th style={{ ...thStyles, ...checkboxCellStyles }}>
                  <Checkbox
                    checked={data.length > 0 && selectedRows.length === data.length}
                    onChange={handleSelectAll}
                    indeterminate={selectedRows.length > 0 && selectedRows.length < data.length}
                  />
                </th>
              )}
              
              {columns.map((column) => (
                <th 
                  key={column.key} 
                  style={{ 
                    ...thStyles, 
                    ...(column.sortable ? sortableThStyles : {}),
                    ...(column.width ? { width: column.width } : {})
                  }}
                  onClick={column.sortable ? () => handleSort(column.key) : undefined}
                >
                  {column.label}
                  {column.sortable && (
                    <span style={sortIconStyles}>
                      <MockIcons.Sort
                        size={16}
                        color={sortConfig.key === column.key 
                          ? currentTheme.colors.secondary.main 
                          : currentTheme.colors.text.disabled}
                      />
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          
          <tbody>
            {displayData.length === 0 ? (
              <tr>
                <td 
                  colSpan={selectable ? columns.length + 1 : columns.length}
                  style={emptyStateStyles}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              displayData.map((row, rowIndex) => (
                <tr 
                  key={row.id || rowIndex}
                  style={trStyles}
                  onClick={() => handleRowClick(row, rowIndex)}
                >
                  {selectable && (
                    <td 
                      style={{ ...tdStyles, ...checkboxCellStyles }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={selectedRows.includes(row.id)}
                        onChange={(e) => handleSelectRow(e, row.id)}
                      />
                    </td>
                  )}
                  
                  {columns.map((column) => (
                    <td key={column.key} style={tdStyles}>
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {pagination && data.length > 0 && (
        <div style={paginationStyles}>
          <div>
            Showing {Math.min((page - 1) * rowsPerPage + 1, data.length)} to {Math.min(page * rowsPerPage, data.length)} of {data.length} entries
          </div>
          
          <div style={paginationButtonsStyles}>
            <button
              style={paginationButtonStyle}
              onClick={() => handlePageChange(1)}
              disabled={page === 1}
            >
              <MockIcons.Chevron direction="left" size={16} />
              <MockIcons.Chevron direction="left" size={16} style={{ marginLeft: -8 }} />
            </button>
            
            <button
              style={paginationButtonStyle}
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              <MockIcons.Chevron direction="left" size={16} />
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
              let pageNumber;
              
              if (totalPages <= 5) {
                pageNumber = i + 1;
              } else if (page <= 3) {
                pageNumber = i + 1;
              } else if (page >= totalPages - 2) {
                pageNumber = totalPages - 4 + i;
              } else {
                pageNumber = page - 2 + i;
              }
              
              if (pageNumber <= totalPages) {
                return (
                  <button
                    key={pageNumber}
                    style={pageNumber === page ? activePageButtonStyle : paginationButtonStyle}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              }
              return null;
            })}
            
            <button
              style={paginationButtonStyle}
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
            >
              <MockIcons.Chevron direction="right" size={16} />
            </button>
            
            <button
              style={paginationButtonStyle}
              onClick={() => handlePageChange(totalPages)}
              disabled={page === totalPages}
            >
              <MockIcons.Chevron direction="right" size={16} />
              <MockIcons.Chevron direction="right" size={16} style={{ marginLeft: -8 }} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const GridView = ({
  items = [],
  renderItem,
  columns = {
    xs: 1,
    sm: 2,
    md: 3,
    lg: 4,
    xl: 5
  },
  spacing = 16,
  onItemClick,
  emptyMessage = 'No items to display',
  loading = false,
  style,
  ...props
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  
  const gridContainerStyles = {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns.xs}, 1fr)`,
    gap: spacing,
    width: '100%',
    position: 'relative',
    // Responsive columns using media queries would be implemented in a real CSS framework
    // This is a simplified version
    ...style
  };
  
  const emptyStateStyles = {
    padding: '48px 0',
    textAlign: 'center',
    color: currentTheme.colors.text.secondary,
    fontSize: '0.875rem',
    gridColumn: '1 / -1'
  };
  
  const loadingOverlayStyles = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: `${currentTheme.colors.background.default}80`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1
  };
  
  return (
    <div style={gridContainerStyles} {...props}>
      {loading && (
        <div style={loadingOverlayStyles}>
          <ProgressBar variant="indeterminate" />
        </div>
      )}
      
      {items.length === 0 ? (
        <div style={emptyStateStyles}>
          {emptyMessage}
        </div>
      ) : (
        items.map((item, index) => (
          <div 
            key={item.id || index}
            onClick={() => onItemClick && onItemClick(item, index)}
          >
            {renderItem ? renderItem(item, index) : (
              <Card>
                <div>{item.title || `Item ${index + 1}`}</div>
              </Card>
            )}
          </div>
        ))
      )}
    </div>
  );
};

const KanbanBoard = ({
  columns = [],
  onCardMove,
  onColumnAdd,
  onCardAdd,
  style,
  ...props
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  const [draggingCard, setDraggingCard] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  
  // Handle drag start
  const handleDragStart = (e, card, sourceColumn) => {
    setDraggingCard({
      card,
      sourceColumnId: sourceColumn.id
    });
  };
  
  // Handle drag over
  const handleDragOver = (e, column) => {
    e.preventDefault();
    setDragOverColumn(column.id);
  };
  
  // Handle drop
  const handleDrop = (e, targetColumn) => {
    e.preventDefault();
    
    if (draggingCard && draggingCard.sourceColumnId !== targetColumn.id) {
      if (onCardMove) {
        onCardMove(
          draggingCard.card,
          draggingCard.sourceColumnId,
          targetColumn.id
        );
      }
    }
    
    setDraggingCard(null);
    setDragOverColumn(null);
  };
  
  const handleAddCard = (columnId) => {
    if (onCardAdd) {
      onCardAdd(columnId);
    }
  };
  
  const boardStyles = {
    display: 'flex',
    overflowX: 'auto',
    padding: '16px',
    minHeight: '400px',
    ...currentTheme.styles.scrollbarStyles,
    ...style
  };
  
  const columnStyles = {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '280px',
    maxWidth: '280px',
    backgroundColor: currentTheme.colors.background.light,
    borderRadius: currentTheme.borderRadius.lg,
    marginRight: '16px',
    flexShrink: 0,
    maxHeight: '100%'
  };
  
  const columnHeaderStyles = {
    padding: '12px 16px',
    borderBottom: `1px solid ${currentTheme.colors.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };
  
  const columnTitleStyles = {
    margin: 0,
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: '1rem',
    fontWeight: 600,
    color: currentTheme.colors.text.primary
  };
  
  const cardCountStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '24px',
    height: '24px',
    borderRadius: '12px',
    backgroundColor: currentTheme.colors.background.lighter,
    color: currentTheme.colors.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '0 8px'
  };
  
  const columnContentStyles = {
    padding: '8px',
    overflowY: 'auto',
    flex: 1,
    ...currentTheme.styles.scrollbarStyles
  };
  
  const cardStyles = {
    padding: '12px',
    backgroundColor: currentTheme.colors.background.card,
    borderRadius: currentTheme.borderRadius.md,
    marginBottom: '8px',
    boxShadow: currentTheme.shadows.sm,
    cursor: 'grab',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: currentTheme.shadows.md
    },
    '&:active': {
      cursor: 'grabbing'
    }
  };
  
  const cardTitleStyles = {
    margin: '0 0 8px 0',
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: '0.875rem',
    fontWeight: 600,
    color: currentTheme.colors.text.primary
  };
  
  const cardDescriptionStyles = {
    margin: 0,
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: '0.75rem',
    color: currentTheme.colors.text.secondary,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  };
  
  const addCardButtonStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    backgroundColor: 'transparent',
    border: `1px dashed ${currentTheme.colors.divider}`,
    borderRadius: currentTheme.borderRadius.md,
    color: currentTheme.colors.text.secondary,
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: '0.875rem',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'background-color 0.2s ease, color 0.2s ease',
    '&:hover': {
      backgroundColor: currentTheme.colors.background.paper,
      color: currentTheme.colors.primary.main
    }
  };
  
  const addColumnButtonStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '280px',
    maxWidth: '280px',
    height: '80px',
    backgroundColor: currentTheme.colors.background.lighter,
    border: `1px dashed ${currentTheme.colors.divider}`,
    borderRadius: currentTheme.borderRadius.lg,
    color: currentTheme.colors.text.secondary,
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: '0.875rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease, color 0.2s ease',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: currentTheme.colors.background.light,
      color: currentTheme.colors.primary.main
    }
  };
  
  return (
    <div style={boardStyles} {...props}>
      {columns.map((column) => (
        <div 
          key={column.id}
          style={{
            ...columnStyles,
            ...(dragOverColumn === column.id ? {
              backgroundColor: currentTheme.colors.background.lighter,
              boxShadow: `0 0 0 2px ${currentTheme.colors.primary.main}`
            } : {})
          }}
          onDragOver={(e) => handleDragOver(e, column)}
          onDrop={(e) => handleDrop(e, column)}
        >
          <div style={columnHeaderStyles}>
            <h3 style={columnTitleStyles}>{column.title}</h3>
            <span style={cardCountStyles}>{column.cards.length}</span>
          </div>
          
          <div style={columnContentStyles}>
            {column.cards.map((card) => (
              <div
                key={card.id}
                style={cardStyles}
                draggable
                onDragStart={(e) => handleDragStart(e, card, column)}
              >
                <h4 style={cardTitleStyles}>{card.title}</h4>
                {card.description && (
                  <p style={cardDescriptionStyles}>{card.description}</p>
                )}
                {card.tags && card.tags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
                    {card.tags.map((tag, tagIndex) => (
                      <Badge
                        key={tagIndex}
                        size="small"
                        color={tag.color || 'secondary'}
                        variant="soft"
                      >
                        {tag.label}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            <button
              style={addCardButtonStyles}
              onClick={() => handleAddCard(column.id)}
            >
              <MockIcons.Add size={16} style={{ marginRight: '4px' }} />
              Add Card
            </button>
          </div>
        </div>
      ))}
      
      {onColumnAdd && (
        <button
          style={addColumnButtonStyles}
          onClick={onColumnAdd}
        >
          <MockIcons.Add size={20} style={{ marginRight: '8px' }} />
          Add Column
        </button>
      )}
    </div>
  );
};

const CalendarView = ({
  events = [],
  onEventClick,
  onDateClick,
  view = 'month',
  date = new Date(),
  onViewChange,
  onDateChange,
  style,
  ...props
}) => {
  const { theme: currentTheme = AghrbaThemes.dark } = useTheme();
  
  // Helper to get day names
  const getDayNames = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days;
  };
  
  // Helper to get month days
  const getMonthDays = () => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    
    // Get the starting day of the week (0-6, where 0 is Sunday)
    const startingDayOfWeek = firstDay.getDay();
    
    // Generate an array of day objects for the calendar
    const days = [];
    
    // Add days from previous month to fill the first row
    const daysFromPrevMonth = startingDayOfWeek;
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    
    for (let i = prevMonthLastDay - daysFromPrevMonth + 1; i <= prevMonthLastDay; i++) {
      days.push({
        date: new Date(year, month - 1, i),
        isCurrentMonth: false,
        day: i
      });
    }
    
    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true,
        day: i
      });
    }
    
    // Add days from next month to complete the last row
    const daysToAdd = 42 - days.length; // 42 = 6 rows x 7 days
    
    for (let i = 1; i <= daysToAdd; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
        day: i
      });
    }
    
    return days;
  };
  
  // Helper to get week days
  const getWeekDays = () => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    
    // Get the starting day of the week (Sunday)
    const currentDay = new Date(year, month, day);
    const dayOfWeek = currentDay.getDay();
    const startDate = new Date(year, month, day - dayOfWeek);
    
    // Generate an array of 7 days starting from Sunday
    const days = [];
    
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      
      days.push({
        date: currentDate,
        isCurrentMonth: currentDate.getMonth() === month,
        day: currentDate.getDate()
      });
    }
    
    return days;
  };
  
  // Helper to get day hours
  const getDayHours = () => {
    const hours = [];
    
    for (let i = 0; i < 24; i++) {
      const hour = i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`;
      hours.push({ hour, value: i });
    }
    
    return hours;
  };
  
  // Get events for a specific date
  const getEventsForDate = (currentDate) => {
    const dateStr = currentDate.toDateString();
    return events.filter(event => {
      const eventDate = new Date(event.start).toDateString();
      return eventDate === dateStr;
    });
  };
  
  // Get current month name and year
  const getMonthYearHeader = () => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };
  
  // Handle date navigation (prev/next)
  const handleDateChange = (direction) => {
    let newDate;
    
    if (view === 'month') {
      newDate = new Date(date.getFullYear(), date.getMonth() + direction, 1);
    } else if (view === 'week') {
      newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() + (7 * direction));
    } else if (view === 'day') {
      newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() + direction);
    }
    
    if (onDateChange) onDateChange(newDate);
  };
  
  // Handle view change
  const handleViewChange = (newView) => {
    if (onViewChange) onViewChange(newView);
  };
  
  // Handle event click
  const handleEventClick = (event) => {
    if (onEventClick) onEventClick(event);
  };
  
  // Handle date click
  const handleDateClick = (clickedDate) => {
    if (onDateClick) onDateClick(clickedDate);
  };
  
  const calendarStyles = {
    backgroundColor: currentTheme.colors.background.card,
    borderRadius: currentTheme.borderRadius.lg,
    boxShadow: currentTheme.shadows.md,
    overflow: 'hidden',
    ...style
  };
  
  const calendarHeaderStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    borderBottom: `1px solid ${currentTheme.colors.divider}`
  };
  
  const headerTitleStyles = {
    fontFamily: currentTheme.typography.fontFamily.display,
    fontSize: '1.5rem',
    fontWeight: 600,
    color: currentTheme.colors.text.primary,
    margin: 0
  };
  
  const headerActionsStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };
  
  const dayHeaderStyles = {
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: '0.875rem',
    fontWeight: 600,
    color: currentTheme.colors.text.secondary,
    padding: '8px',
    textAlign: 'center',
    borderBottom: `1px solid ${currentTheme.colors.divider}`
  };
  
  const dayContainerStyles = {
    height: '120px',
    padding: '8px',
    borderRight: `1px solid ${currentTheme.colors.divider}`,
    borderBottom: `1px solid ${currentTheme.colors.divider}`,
    overflow: 'hidden',
    backgroundColor: 'transparent',
    transition: 'background-color 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: currentTheme.colors.background.subtle
    }
  };
  
  const dayNumberStyles = {
    fontFamily: currentTheme.typography.fontFamily.primary,
    fontSize: '0.875rem',
    fontWeight: 500,
    color: currentTheme.colors.text.primary,
    marginBottom: '4px'
  };
  
  const dimmedDayStyles = {
    color: currentTheme.colors.text.disabled,
    backgroundColor: currentTheme.colors.background.light
  };
  
  const todayStyles = {
    backgroundColor: `${currentTheme.colors.primary.main}10`,
    fontWeight: 700
  };
  
  const eventStyles = {
    fontSize: '0.75rem',
    padding: '2px 6px',
    borderRadius: currentTheme.borderRadius.sm,
    marginBottom: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    transition: 'filter 0.2s ease',
    '&:hover': {
      filter: 'brightness(1.1)'
    }
  };
  
  const weekViewStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    height: '600px',
    overflow: 'auto',
    ...currentTheme.styles.scrollbarStyles
  };
  
  const dayViewStyles = {
    display: 'flex',
    flexDirection: 'column',
    height: '600px',
    overflow: 'auto',
    ...currentTheme.styles.scrollbarStyles
  };
  
  const hourCellStyles = {
    display: 'flex',
    minHeight: '60px',
    borderBottom: `1px solid ${currentTheme.colors.divider}`,
    position: 'relative'
  };
  
  const hourLabelStyles = {
    width: '60px',
    padding: '8px',
    fontSize: '0.75rem',
    color: currentTheme.colors.text.secondary,
    textAlign: 'right',
    borderRight: `1px solid ${currentTheme.colors.divider}`
  };
  
  const hourContentStyles = {
    flex: 1,
    padding: '4px',
    position: 'relative'
  };
  
  // Render the calendar based on the view
  const renderCalendar = () => {
    if (view === 'month') {
      const days = getMonthDays();
      const dayNames = getDayNames();
      
      return (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {dayNames.map((day, index) => (
              <div key={index} style={dayHeaderStyles}>
                {day}
              </div>
            ))}
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {days.map((day, index) => {
              const isToday = day.date.toDateString() === new Date().toDateString();
              const dayEvents = getEventsForDate(day.date);
              
              return (
                <div 
                  key={index} 
                  style={{
                    ...dayContainerStyles,
                    ...(isToday ? todayStyles : {}),
                    ...(!day.isCurrentMonth ? dimmedDayStyles : {})
                  }}
                  onClick={() => handleDateClick(day.date)}
                >
                  <div style={dayNumberStyles}>{day.day}</div>
                  
                  <div>
                    {dayEvents.slice(0, 3).map((event, eventIndex) => (
                      <div 
                        key={eventIndex}
                        style={{
                          ...eventStyles,
                          backgroundColor: event.color || currentTheme.colors.primary.main,
                          color: event.textColor || '#ffffff'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEventClick(event);
                        }}
                      >
                        {event.title}
                      </div>
                    ))}
                    
                    {dayEvents.length > 3 && (
                      <div style={{
                        fontSize: '0.75rem',
                        color: currentTheme.colors.text.secondary
                      }}>
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    } else if (view === 'week') {
      const days = getWeekDays();
      const dayNames = getDayNames();
      
      return (
        <div style={weekViewStyles}>
          {dayNames.map((dayName, index) => (
            <div key={index} style={dayHeaderStyles}>
              <div>{dayName}</div>
              <div style={{
                fontWeight: days[index].date.toDateString() === new Date().toDateString() ? 700 : 500
              }}>
                {days[index].day}
              </div>
            </div>
          ))}
          
          {days.map((day, dayIndex) => {
            const dayEvents = getEventsForDate(day.date);
            
            return (
              <div 
                key={dayIndex}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  borderRight: dayIndex < 6 ? `1px solid ${currentTheme.colors.divider}` : 'none',
                }}
              >
                {dayEvents.map((event, eventIndex) => (
                  <div 
                    key={eventIndex}
                    style={{
                      ...eventStyles,
                      backgroundColor: event.color || currentTheme.colors.primary.main,
                      color: event.textColor || '#ffffff',
                      margin: '4px',
                      padding: '4px 8px',
                    }}
                    onClick={() => handleEventClick(event)}
                  >
                    {event.title}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      );
    } else if (view === 'day') {
      const hours = getDayHours();
      const dayEvents = getEventsForDate(date);
      
      return (
        <div style={dayViewStyles}>
          {hours.map((hour, index) => (
            <div key={index} style={hourCellStyles}>
              <div style={hourLabelStyles}>{hour.hour}</div>
              <div style={hourContentStyles}>
                {dayEvents
                  .filter(event => {
                    const eventHour = new Date(event.start).getHours();
                    return eventHour === hour.value;
                  })
                  .map((event, eventIndex) => (
                    <div 
                      key={eventIndex}
                      style={{
                        ...eventStyles,
                        backgroundColor: event.color || currentTheme.colors.primary.main,
                        color: event.textColor || '#ffffff',
                        padding: '4px 8px',
                        marginBottom: '4px'
                      }}
                      onClick={() => handleEventClick(event)}
                    >
                      {event.title}
                    </div>
                  ))
                }
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    return null;
  };
  
  return (
    <div style={calendarStyles} {...props}>
      <div style={calendarHeaderStyles}>
        <h2 style={headerTitleStyles}>{getMonthYearHeader()}</h2>
        
        <div style={headerActionsStyles}>
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => handleViewChange('day')}
            style={{ 
              backgroundColor: view === 'day' ? currentTheme.colors.primary.main : 'transparent',
              color: view === 'day' ? currentTheme.colors.primary.contrastText : currentTheme.colors.primary.main
            }}
          >
            Day
          </Button>
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => handleViewChange('week')}
            style={{ 
              backgroundColor: view === 'week' ? currentTheme.colors.primary.main : 'transparent',
              color: view === 'week' ? currentTheme.colors.primary.contrastText : currentTheme.colors.primary.main
            }}
          >
            Week
          </Button>
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => handleViewChange('month')}
            style={{ 
              backgroundColor: view === 'month' ? currentTheme.colors.primary.main : 'transparent',
              color: view === 'month' ? currentTheme.colors.primary.contrastText : currentTheme.colors.primary.main
            }}
          >
            Month
          </Button>
          
          <div style={{ width: '16px' }} />
          
          <IconButton 
            size="small"
            onClick={() => handleDateChange(-1)}
          >
            <MockIcons.Chevron direction="left" />
          </IconButton>
          <Button 
            variant="text" 
            size="small"
            onClick={() => onDateChange && onDateChange(new Date())}
          >
            Today
          </Button>
          <IconButton 
            size="small"
            onClick={() => handleDateChange(1)}
          >
            <MockIcons.Chevron direction="right" />
          </IconButton>
        </div>
      </div>
      
      <div>
        {renderCalendar()}
      </div>
    </div>
  )}

  export default AghrbaDesignSystem